import type { DashboardPageId, WidgetLayout, OnboardingState, TourKey } from '@proadv/types';

/**
 * Cliente HTTP da SPA web.
 *
 * Decisoes (docs/modules/web-v2-auth.md):
 * - Toda request usa credentials: 'include' (cookie httpOnly da sessao).
 * - Anexa Authorization: Bearer <accessToken> se houver token em memoria.
 * - Em 401, dispara um unico refresh (mutex) e repete a request original
 *   uma vez. Se o refresh tambem falhar com 401, sign-out forcado +
 *   redirect para /sign-in?reason=session_expired.
 * - Lanca ApiError com { status, body, message } em qualquer nao-2xx.
 *
 * Para evitar dependencia circular com auth-store (que importa este
 * arquivo), a leitura/escrita do accessToken e o sign-out forcado sao
 * feitos via callbacks injetaveis (configureApiClient). Por padrao, no
 * primeiro acesso, o api-client se conecta ao useAuthStore via lazy
 * require — nao precisa de boot manual.
 */

const RAW_API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '');

if (!RAW_API_URL) {
  // Build de producao sem VITE_API_URL e bug critico — descobrimos isso quando
  // o login mostrou "Failed to fetch" porque o bundle tentava localhost:3001.
  // Em dev (import.meta.env.DEV === true), permitimos fallback explicito para
  // facilitar o dev loop; em build/preview/prod, falha alto e cedo.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn('[api-client] VITE_API_URL não setado, usando http://localhost:3001 (dev only)');
  } else {
    throw new Error(
      '[api-client] VITE_API_URL é obrigatório em build de produção. ' +
      'Defina em apps/web/.env.production ou exporte antes de rodar `pnpm build`.',
    );
  }
}

const API_URL = RAW_API_URL ?? 'http://localhost:3001';

if (!import.meta.env.DEV && API_URL.includes('localhost')) {
  throw new Error(`[api-client] API_URL aponta para localhost em build não-dev: ${API_URL}`);
}

export interface ApiError extends Error {
  status: number;
  body: unknown;
  code?: string;
}

function buildError(status: number, body: unknown, path: string): ApiError {
  const message =
    (typeof body === 'object' &&
      body !== null &&
      'message' in body &&
      typeof (body as { message?: unknown }).message === 'string'
      ? (body as { message: string }).message
      : null) ?? `API ${status} em ${path}`;
  const err = new Error(message) as ApiError;
  err.status = status;
  err.body = body;
  if (
    typeof body === 'object' &&
    body !== null &&
    'code' in body &&
    typeof (body as { code?: unknown }).code === 'string'
  ) {
    err.code = (body as { code: string }).code;
  }
  return err;
}

function safeJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function numHeader(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Hooks injetaveis (preenchidos pelo auth-store no primeiro uso)
// ---------------------------------------------------------------------------

interface ApiClientHooks {
  getAccessToken(): string | null;
  setAccessToken(token: string): void;
  onSessionLost(reason: 'refresh_failed' | 'no_session'): void;
  /**
   * Bloqueio por inadimplencia (HTTP 402, code SUBSCRIPTION_BLOCKED) em
   * QUALQUER request de dominio — cobre o bloqueio acontecendo no meio da
   * sessao, sem esperar o proximo bootstrap. `reason` vem cru do body
   * (o auth-store normaliza para a uniao conhecida).
   */
  onSubscriptionBlocked(reason: string | null): void;
}

let hooks: ApiClientHooks | null = null;

export function configureApiClient(h: ApiClientHooks): void {
  hooks = h;
}

function getHooks(): ApiClientHooks {
  if (hooks) return hooks;
  // Lazy bind ao auth-store (evita ciclo de modulo no toplevel).
  // Importacao dinamica sincrona via require nao funciona em ESM; usamos
  // um proxy que so resolve quando configureApiClient nao foi chamado
  // explicitamente. Em pratica, main.tsx chama configureApiClient antes
  // de qualquer request — esse fallback e defensivo.
  throw new Error(
    'apiClient não configurado: chame configureApiClient(hooks) no bootstrap',
  );
}

/**
 * Leitura defensiva do access token em memoria, para clientes fora do
 * `apiClient` (ex: o `authedFetch` do streaming do assistente). Reusa o
 * mesmo hook do auth-store; retorna null se ainda nao configurado.
 */
export function getAccessTokenSafe(): string | null {
  try {
    return getHooks().getAccessToken();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Mutex de refresh
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { accessToken?: string };
      return body.accessToken ?? null;
    } catch {
      return null;
    } finally {
      // libera o slot no proximo tick para que callers concorrentes
      // que ja peguem a promise terminem antes de uma nova tentativa.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// Core call
// ---------------------------------------------------------------------------

interface CallOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  /** Pula tratamento automatico de 401 (usado pelo proprio refresh/me). */
  skipAuthRefresh?: boolean;
  /** Inclui Authorization mesmo sem token? Default true. */
  includeAuth?: boolean;
}

async function call<T>(path: string, opts: CallOptions = {}): Promise<T> {
  return doCall<T>(path, opts, false);
}

/** Upload multipart/form-data sem Content-Type manual (browser define boundary) */
async function callFormData<T>(path: string, formData: FormData): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  try {
    const token = getHooks().getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    /* hooks nao configurados */
  }
  const res = await fetch(`${API_URL}/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });
  const text = await res.text();
  const body = safeJson(text);
  if (res.ok) return body as T;
  throw buildError(res.status, body, path);
}

async function doCall<T>(
  path: string,
  opts: CallOptions,
  isRetry: boolean,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(opts.headers ?? {}),
  };

  if (opts.includeAuth !== false) {
    try {
      const token = getHooks().getAccessToken();
      if (token && !headers.Authorization) {
        headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      /* hooks nao configurados ainda — segue sem Authorization */
    }
  }

  const res = await fetch(`${API_URL}/api${path}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  // 204
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = safeJson(text);

  if (res.ok) {
    return body as T;
  }

  // 402 — bloqueio por inadimplencia (rotas de dominio). Nao e erro de auth
  // (sem retry/refresh): forca o estado de bloqueio global e deixa o erro
  // propagar normalmente pro caller (o gate de rota cuida do redirect).
  if (res.status === 402) {
    const code =
      typeof body === 'object' && body !== null && 'code' in body
        ? (body as { code?: unknown }).code
        : undefined;
    if (code === 'SUBSCRIPTION_BLOCKED') {
      const reason =
        typeof body === 'object' && body !== null && 'reason' in body
          ? (body as { reason?: unknown }).reason
          : undefined;
      try {
        getHooks().onSubscriptionBlocked(typeof reason === 'string' ? reason : null);
      } catch {
        /* hooks nao configurados ainda */
      }
    }
  }

  // 401 com refresh automatico
  if (res.status === 401 && !opts.skipAuthRefresh && !isRetry) {
    const newToken = await performRefresh();
    if (newToken) {
      try {
        getHooks().setAccessToken(newToken);
      } catch {
        /* ignora */
      }
      return doCall<T>(path, opts, true);
    }
    // Refresh falhou — sessao perdida.
    try {
      getHooks().onSessionLost('refresh_failed');
    } catch {
      /* ignora */
    }
  }

  throw buildError(res.status, body, path);
}

// ---------------------------------------------------------------------------
// Tipos de resposta
// ---------------------------------------------------------------------------

// Platform settings (aparencia global)
export interface PlatformBranding {
  brandingEnabled: boolean;
  headerLogoUrl: string | null;
  headerLogoSizePx: number;
  headerName: string | null;
  headerTextColor: string;
  loginLogoUrl: string | null;
  loginLogoSizePx: number;
  faviconUrl: string | null;
  collapsedLogoUrl: string | null;
  collapsedLogoSizePx: number;
  /** F12 Billing — dias de tolerancia antes de bloquear escritorio inadimplente (0-90). */
  pastDueGraceDays: number;
}

export interface PlatformSettingsUpdate {
  headerLogoSizePx?: number;
  headerName?: string | null;
  headerTextColor?: string;
  loginLogoSizePx?: number;
  brandingEnabled?: boolean;
  collapsedLogoSizePx?: number;
  pastDueGraceDays?: number;
}

export type PlatformAssetKind = 'header-logo' | 'login-logo' | 'favicon' | 'collapsed-logo';

export interface PublicUser {
  id: string;
  email: string;
  name: string;
  oab?: string | null;
  status?: string;
  emailVerifiedAt?: string | null;
  mfaEnabled: boolean;
  isSuperAdmin?: boolean;
  createdAt?: string;
}

/** Bloqueio por inadimplencia — irmao de tenant/currentMembership no /me. */
export interface MeBillingInfo {
  blocked: boolean;
  reason: 'past_due' | 'suspended' | 'canceled' | null;
  graceEndsAt: string | null;
}

export interface MeResponse {
  user: PublicUser;
  accessToken: string;
  tenantId: string | null;
  tenant: { id: string; slug: string; name: string } | null;
  isSuperAdmin: boolean;
  mfaRequired: false;
  memberships: Array<{
    tenantId: string;
    slug: string;
    name: string;
    role: string;
  }>;
  currentMembership?: {
    legacyRole: string;
    allowedModules: AllowedModule[] | null;
  } | null;
  billing: MeBillingInfo | null;
  expiresAt: string;
  onboarding: OnboardingState;
}

export interface SignInResponse {
  user: PublicUser;
  session?: { id: string; expiresAt: string };
  accessToken?: string;
  refreshToken?: string;
  tenantId?: string | null;
  mfaRequired: boolean;
  mfaChallengeId?: string;
}

export interface VerifyMfaLoginResponse {
  user: PublicUser;
  session: { id: string; expiresAt: string };
  accessToken: string;
  refreshToken: string;
  tenantId: string | null;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  tenantId: string | null;
}

export interface SwitchTenantResponse {
  accessToken: string;
  refreshToken: string;
  tenantId?: string | null;
}

export interface TenantSummaryResponse {
  id: string;
  slug: string;
  name: string;
  status: 'active' | 'suspended' | 'trial' | 'canceled';
  legacyRole:
    | 'owner'
    | 'admin'
    | 'lawyer'
    | 'assistant'
    | 'financial'
    | 'client_read_only';
  joinedAt: string;
}

export interface TenantResponse {
  id: string;
  slug: string;
  name: string;
  status: 'active' | 'suspended' | 'trial' | 'canceled';
  settings: Record<string, unknown>;
  trialEndsAt: string | null;
  createdAt: string;
}

// Espelha VALID_MODULES em packages/database/src/schema/tenants.ts.
// 'assistant' faltava aqui, então a UI nunca conseguia conceder o módulo do
// assistente de IA a um member, e qualquer save de usuário o removia da lista.
// Sem isso, ligar o gate de @RequireModule tiraria o assistente de todos os
// members sem o admin ter como devolver.
export type AllowedModule =
  | 'clients'
  | 'cases'
  | 'publications'
  | 'agenda'
  | 'documents'
  | 'financial'
  | 'reports'
  | 'assistant';

export interface MembershipWithUserResponse {
  id: string;
  tenantId: string;
  userId: string;
  legacyRole: TenantSummaryResponse['legacyRole'];
  isAdmin: boolean;
  allowedModules: AllowedModule[] | null;
  joinedAt: string;
  user: { id: string; email: string; name: string };
}

// ---------------------------------------------------------------------------
// F03 — Visibility grants (concessoes de visibilidade viewer -> owner)
// ---------------------------------------------------------------------------

export type VisibilityGrantModule = 'cases' | 'tasks' | 'clients';

export interface VisibilityGrantResponse {
  id: string;
  tenantId: string;
  viewerUserId: string;
  viewerName: string;
  ownerUserId: string;
  ownerName: string;
  module: VisibilityGrantModule;
  grantedByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVisibilityGrantInput {
  viewerUserId: string;
  ownerUserId: string;
  modules: VisibilityGrantModule[];
}

export interface CreateTenantUserInput {
  name: string;
  email: string;
  password: string;
  isAdmin?: boolean;
  allowedModules?: AllowedModule[];
}

export interface UpdateTenantMemberInput {
  isAdmin?: boolean;
  allowedModules?: AllowedModule[];
}

export interface InviteResponse {
  id: string;
  tenantId: string;
  email: string;
  legacyRole: TenantSummaryResponse['legacyRole'];
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

export interface InviteMemberResponse {
  invite: InviteResponse;
  tokenForOperator: string;
}

export interface AdminTenantSummary {
  id: string;
  slug: string;
  name: string;
  status: 'active' | 'suspended' | 'trial' | 'canceled';
  settings: Record<string, unknown>;
  trialEndsAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  memberCount: number;
}

export interface AdminTenantMember {
  membershipId: string;
  userId: string;
  email: string;
  name: string;
  legacyRole: TenantSummaryResponse['legacyRole'];
  joinedAt: string;
}

export interface AdminTenantDetail extends AdminTenantSummary {
  members: AdminTenantMember[];
}

export interface CreateTenantWithOwnerInput {
  tenantName: string;
  tenantSlug: string;
  ownerName: string;
  ownerEmail: string;
}

export interface CreateTenantWithOwnerResponse {
  tenant: {
    id: string;
    slug: string;
    name: string;
    status: 'active' | 'suspended' | 'trial' | 'canceled';
  };
  owner: { id: string; email: string; name: string };
  firstAccessUrl: string;
}

export interface FirstAccessPreviewResponse {
  ownerEmail: string;
  ownerName: string;
  tenantName: string;
  tenantSlug: string;
}

// Integracoes globais da plataforma
export interface PluggyIntegrationStatus {
  configured: boolean;
  enabled: boolean;
  clientIdMasked: string | null;
}

export interface UpdatePluggyIntegrationInput {
  clientId: string;
  clientSecret?: string;
  enabled: boolean;
}

export interface OpenAiIntegrationStatus {
  configured: boolean;
  enabled: boolean;
  keyMasked: string | null;
}

export interface UpdateOpenAiIntegrationInput {
  apiKey?: string;
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// Modelos de IA por finalidade (config de plataforma).
// Espelha apps/api/src/modules/ai/ai-model-catalog.ts.
// ---------------------------------------------------------------------------

export type AiPurpose = 'conversation' | 'vision' | 'transcription';
export type AiProviderId = 'openai';

export interface AiModelSelection {
  provider: AiProviderId;
  model: string;
}

/** Selecao efetiva; `source` diz se veio do admin, de env ou do default. */
export interface ResolvedAiModel extends AiModelSelection {
  purpose: AiPurpose;
  source: 'config' | 'env' | 'default';
}

export interface AiModelsIntegrationStatus {
  configured: boolean;
  purposes: Record<AiPurpose, ResolvedAiModel>;
  defaults: Record<AiPurpose, AiModelSelection>;
}

export type UpdateAiModelsIntegrationInput = Record<AiPurpose, AiModelSelection>;

/** Configuracao SMTP global da plataforma (e-mails transacionais do sistema). */
export interface EmailSmtpIntegrationStatus {
  configured: boolean;
  enabled: boolean;
  host: string | null;
  port: number | null;
  secure: boolean;
  username: string | null;
  from: string | null;
  passwordConfigured: boolean;
}

export interface UpdateEmailSmtpIntegrationInput {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  /** Ausente/vazio = mantem a senha ja gravada. */
  password?: string;
  from: string;
  enabled: boolean;
}

export interface TestEmailSmtpIntegrationResult {
  ok: boolean;
  message: string;
}

/**
 * Storage S3 + quotas (rodada 3b) — config S3-compatível (MinIO) da
 * plataforma. Espelha StorageS3IntegrationStatusDto (apps/api/src/modules/
 * admin/dto/storage-s3-integration.dto.ts). `secretKeyConfigured` indica se
 * há uma secret já gravada; a secret em si nunca volta da API.
 */
export interface StorageS3IntegrationStatus {
  configured: boolean;
  enabled: boolean;
  endpoint: string | null;
  region: string | null;
  bucket: string | null;
  accessKeyMasked: string | null;
  secretKeyConfigured: boolean;
}

export interface UpdateStorageS3IntegrationInput {
  endpoint: string;
  region: string;
  bucket: string;
  accessKey: string;
  /** Ausente/vazio = mantem a secret ja gravada. */
  secretKey?: string;
  enabled: boolean;
}

export interface TestStorageS3IntegrationResult {
  ok: boolean;
  message: string;
}

export interface FirstAccessActivateResponse {
  user: PublicUser;
  session: { id: string; expiresAt: string };
  accessToken: string;
  refreshToken: string;
  tenantId: string;
}

export interface InvitePreviewResponse {
  tenantName: string;
  tenantSlug: string;
  inviteeEmail: string;
  legacyRole: TenantSummaryResponse['legacyRole'];
  invitedByName: string | null;
  expiresAt: string;
}

export interface AcceptInviteResponse {
  tenant: TenantResponse;
  membership: { id: string; tenantId: string; userId: string };
  accessToken: string;
  refreshToken: string;
}

export interface SignUpAndAcceptInviteResponse {
  user: { id: string; email: string; name: string };
  session: { id: string };
  tenant: TenantResponse;
  membership: { id: string; tenantId: string; userId: string };
  accessToken: string;
  refreshToken: string;
}

export interface SignUpInput {
  name: string;
  email: string;
  password: string;
  oab?: string;
}

export interface SignUpResponse {
  user: PublicUser;
}

export interface EnableMfaResponse {
  secret: string;
  otpauthUrl: string;
  recoveryCodes: string[];
}

// ---------------------------------------------------------------------------
// D01 Clientes
// ---------------------------------------------------------------------------

export type ClientType = 'pf' | 'pj';
export type ClientStatus =
  | 'lead'
  | 'consultation_paid'
  | 'active'
  | 'inactive';

export type ContactType = 'phone' | 'email' | 'address' | 'whatsapp';
export type ContactLabel =
  | 'main'
  | 'work'
  | 'billing'
  | 'personal'
  | 'other';

export interface ClientContact {
  id: string;
  tenantId: string;
  clientId: string;
  type: ContactType;
  label: ContactLabel;
  value: string;
  isPrimary: boolean;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ClientNote {
  id: string;
  tenantId: string;
  clientId: string;
  content: string;
  pinned: boolean;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type ClientSex = 'M' | 'F' | 'other' | 'prefer_not_to_say';
export type ClientMaritalStatus =
  | 'single'
  | 'married'
  | 'divorced'
  | 'widowed'
  | 'stable_union'
  | 'separated';

export interface Client {
  id: string;
  tenantId: string;
  type: ClientType;
  document: string | null;
  name: string;
  tradeName: string | null;
  status: ClientStatus;
  source: string | null;
  tags: string[];
  customFields: Record<string, unknown>;
  birthDate: string | null;
  profession: string | null;
  city: string | null;
  state: string | null;
  // v2
  sex: ClientSex | null;
  maritalStatus: ClientMaritalStatus | null;
  nationality: string | null;
  motherName: string | null;
  rg: string | null;
  pisPasep: string | null;
  ctps: string | null;
  cid: string | null;
  activity: string | null;
  country: string | null;
  cep: string | null;
  street: string | null;
  streetNumber: string | null;
  complement: string | null;
  neighborhood: string | null;
  generalNotes: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ClientDetailResponse extends Client {
  contacts: ClientContact[];
  notes: ClientNote[];
}

// Portal do Cliente — lado do escritorio (docs/PORTAL-CLIENTE.md 4.1/4.6)
export type PortalAccountStatus = 'invited' | 'active' | 'suspended';

export interface PortalAccessStatusResponse {
  hasAccount: boolean;
  account: {
    id: string;
    email: string;
    status: PortalAccountStatus;
    lastSeenAt: string | null;
  } | null;
  lastLink: {
    sentAt: string;
    expiresAt: string;
    consumedAt: string | null;
    ok: boolean | null;
    error: string | null;
  } | null;
  suggestedEmail: string | null;
}

export interface PortalPreviewStartResponse {
  /** Caminho relativo do portal (`/portal/<slug>`) — abrir em aba nova no proprio origin. */
  portalPath: string;
  expiresAt: string;
}

export interface ClientListItem extends Client {
  primaryContact: { type: string; value: string } | null;
}

export interface ClientsListResponse {
  items: ClientListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ClientAttachment {
  id: string;
  tenantId: string;
  clientId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storageProvider: string;
  storageKey: string | null;
  storageObjectId: string | null;
  uploadedByUserId: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateClientContactInput {
  type: ContactType;
  label: ContactLabel;
  value: string;
  isPrimary: boolean;
}

export interface CreateClientInput {
  type: ClientType;
  name: string;
  document?: string | null;
  tradeName?: string | null;
  status?: ClientStatus;
  source?: string | null;
  tags?: string[];
  customFields?: Record<string, unknown>;
  birthDate?: string | null;
  profession?: string | null;
  city?: string | null;
  state?: string | null;
  sex?: ClientSex | null;
  maritalStatus?: ClientMaritalStatus | null;
  nationality?: string | null;
  motherName?: string | null;
  rg?: string | null;
  pisPasep?: string | null;
  ctps?: string | null;
  cid?: string | null;
  activity?: string | null;
  country?: string | null;
  cep?: string | null;
  street?: string | null;
  streetNumber?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  generalNotes?: string | null;
  contacts?: CreateClientContactInput[];
}

export type UpdateClientInput = Partial<Omit<CreateClientInput, 'type' | 'contacts'>> & {
  documentTypeHint?: ClientType;
};

export interface CreateContactInput {
  type: ContactType;
  label: ContactLabel;
  value: string;
  isPrimary: boolean;
}

export type UpdateContactInput = Partial<CreateContactInput>;

export interface UploadAttachmentResult {
  attachment: ClientAttachment;
  /** Bytes em uso por este tenant apos o upload. */
  storageUsedBytes: number | null;
  /** Limite total do tenant. */
  storageQuotaBytes: number | null;
}

export interface ClientsSummaryResponse {
  totalContacts: number;
  withActiveProcess: number;
  withoutActiveProcess: number;
  /** Crescimento (%) versus o mes anterior. Numero pode ser negativo. */
  growthVsLastMonth: number;
}

export interface SourceCount {
  source: string;
  count: number;
}

export interface AgeRangeCount {
  range:
    | '0-17'
    | '18-25'
    | '26-35'
    | '36-50'
    | '51-65'
    | '65+'
    | 'nao_informado';
  count: number;
}

export interface ProfessionCount {
  profession: string;
  count: number;
}

export interface ClientsChartsResponse {
  topSources: SourceCount[];
  ageRanges: AgeRangeCount[];
  topProfessions: ProfessionCount[];
}

export interface ClientsBirthdaysResponse {
  count: number;
  items: Array<{ id: string; name: string; birthDate: string }>;
}

// ---------------------------------------------------------------------------
// API publica
// ---------------------------------------------------------------------------

// Office config (configuracoes do escritorio)
export interface ActionGroup {
  id: string;
  tenantId: string;
  name: string;
  responsiblePublicationsUserId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateActionGroupInput {
  name: string;
  responsiblePublicationsUserId?: string | null;
  active?: boolean;
}

export type UpdateActionGroupInput = Partial<CreateActionGroupInput>;

export interface ActionType {
  id: string;
  tenantId: string;
  actionGroupId: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateActionTypeInput {
  actionGroupId: string;
  name: string;
  active?: boolean;
}

export type UpdateActionTypeInput = Partial<CreateActionTypeInput>;

export type CaseStagePhase =
  | 'judicial'
  | 'consultoria'
  | 'administrativo'
  | 'negociacao'
  | 'extrajudicial';

export interface CaseStage {
  id: string;
  tenantId: string;
  name: string;
  phase: CaseStagePhase;
  classification: string | null;
  order: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateCaseStageInput {
  name: string;
  phase: CaseStagePhase;
  classification?: string | null;
  order?: number;
  active?: boolean;
}

export type UpdateCaseStageInput = Partial<CreateCaseStageInput>;

export type ActiveFilter = 'true' | 'false' | 'all';

// ---------------------------------------------------------------------------
// D02 Processos
// ---------------------------------------------------------------------------

export type CaseNature =
  | 'consultoria'
  | 'extrajudicial'
  | 'judicial'
  | 'administrativo';

export type CaseStatus = 'active' | 'archived' | 'suspended' | 'closed';

export type CasePartyRole =
  | 'autor'
  | 'reu'
  | 'terceiro_interessado'
  | 'requerente'
  | 'requerido'
  | 'interessado';

export type CaseMovementSource =
  | 'manual'
  | 'djen'
  | 'datajud'
  | 'tribunal'
  | 'imported';

export interface Case {
  id: string;
  tenantId: string;
  nature: CaseNature;
  numeroCnj: string | null;
  actionGroupId: string;
  actionTypeId: string;
  currentStageId: string | null;
  tribunalCode: string | null;
  vara: string | null;
  responsibleUserId: string;
  value: string | null;
  contingencyAmount: string | null;
  status: CaseStatus;
  openedAt: string | null;
  closedAt: string | null;
  closedReason: string | null;
  customFields: Record<string, unknown>;
  generalNotes: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  // Campos novos (migration 0047)
  yearFiled: number | null;
  judiciarySegment: string | null;
  comarca: string | null;
  electronicSystem: string | null;
  parentCaseId: string | null;
  internalReference: string | null;
  attorneyFees: string | null;
  attorneyFeesPercent: string | null;
}

export interface CaseParty {
  id: string;
  tenantId: string;
  caseId: string;
  clientId: string;
  role: CasePartyRole;
  isMain: boolean;
  clientName: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CaseMovementDocumentView {
  id: string;
  sigla: string;
  nome: string | null;
  tipo: string | null;
  sigilo: string | null;
  tamanhoBytes: number | null;
  downloadedAt: string | null;
  storageObjectId: string | null;
  contentType: string | null;
}

export interface CaseMovement {
  id: string;
  tenantId: string;
  caseId: string;
  source: CaseMovementSource;
  occurredAt: string;
  code: string | null;
  description: string;
  rawPayload: unknown;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  documentos: CaseMovementDocumentView[];
}

export interface CaseDetailResponse extends Case {
  parties: CaseParty[];
  movements: CaseMovement[];
  actionGroupName: string | null;
  actionTypeName: string | null;
  currentStageName: string | null;
  responsibleUserName: string | null;
}

// ---------------------------------------------------------------------------
// Portal do Cliente — lado do escritorio (docs/PORTAL-CLIENTE.md secao 3.3/5.1)
// ---------------------------------------------------------------------------

export type PortalOverrideResourceType =
  | 'case_movement'
  | 'document'
  | 'task'
  | 'financial_transaction'
  | 'storage_object';

export type PortalOverrideEffect = 'allow' | 'deny';

export interface PortalCaseAccessView {
  id: string;
  clientId: string;
  clientName: string;
  shareMovements: boolean;
  shareDocuments: boolean;
  shareDeadlines: boolean;
  shareFinancial: boolean;
  grantedByUserId: string;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CaseAccessFlagsInput {
  shareMovements: boolean;
  shareDocuments: boolean;
  shareDeadlines: boolean;
  shareFinancial: boolean;
}

export interface PortalMovementOverrideView {
  clientId: string;
  resourceId: string;
  effect: PortalOverrideEffect;
  /** Marco 3 ampliou o GET de `case_movement` para tambem `document` — ver `PortalCaseAccessService.listMovementOverrides`. */
  resourceType: 'case_movement' | 'document';
}

export interface CaseListItem extends Case {
  actionTypeName: string | null;
  currentStageName: string | null;
  mainPartyNames: string;
}

export interface CasesListResponse {
  items: CaseListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ClientCaseSummary {
  id: string;
  numeroCnj: string | null;
  status: CaseStatus;
  actionTypeName: string | null;
  currentStageName: string | null;
  mainPartyNames: string;
  openedAt: string | null;
}

// J10 — Tribunal Registry (catalogo global, somente leitura)

export type TribunalLevel =
  | 'superior'
  | 'federal'
  | 'estadual'
  | 'trabalhista'
  | 'militar'
  | 'eleitoral';

export type TribunalSystem =
  | 'pje'
  | 'esaj'
  | 'eproc'
  | 'projudi'
  | 'tucujuris'
  | 'themis'
  | 'other';

export interface Tribunal {
  code: string;
  name: string;
  abbreviation: string;
  state: string | null;
  level: TribunalLevel;
  primarySystem: TribunalSystem | null;
  publicQueryUrl: string | null;
  datajudEndpoint: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CasesSummaryResponse {
  totalActive: number;
  totalArchived: number;
  totalSuspended: number;
  totalClosed: number;
}

// J02 — DataJud lookup
export interface DataJudLookupResponse {
  numeroCnj: string;
  tribunalCode: string;
  tribunalAbbreviation: string | null;
  classe: { codigo: number | null; nome: string | null } | null;
  assuntoPrincipal: { codigo: number | null; nome: string | null } | null;
  assuntos: Array<{ codigo: number | null; nome: string | null }>;
  orgaoJulgador: string | null;
  grau: string | null;
  sistema: string | null;
  formato: string | null;
  dataAjuizamento: string | null;
  valorCausa: number | null;
  movimentos: Array<{
    codigo: number | null;
    nome: string;
    dataHora: string;
    complementosTabelados?: Array<{
      codigo: number | null;
      valor: number | null;
      nome: string | null;
      descricao: string | null;
    }>;
  }>;
  cachedAt: string;
  fromCache: boolean;
}

export interface CreateCasePartyInput {
  clientId: string;
  role: CasePartyRole;
  isMain: boolean;
}

export interface CreateCaseInput {
  nature: CaseNature;
  actionGroupId: string;
  actionTypeId: string;
  currentStageId?: string | null;
  numeroCnj?: string | null;
  tribunalCode?: string | null;
  vara?: string | null;
  responsibleUserId?: string | null;
  value?: string | null;
  contingencyAmount?: string | null;
  status?: CaseStatus;
  openedAt?: string | null;
  closedReason?: string | null;
  customFields?: Record<string, unknown>;
  generalNotes?: string | null;
  parties: CreateCasePartyInput[];
  // Campos novos (migration 0047)
  yearFiled?: number | null;
  judiciarySegment?: string | null;
  comarca?: string | null;
  electronicSystem?: string | null;
  parentCaseId?: string | null;
  internalReference?: string | null;
  attorneyFees?: string | null;
  attorneyFeesPercent?: string | null;
}

export type UpdateCaseInput = Partial<Omit<CreateCaseInput, 'parties'>>;

export interface CreateMovementInput {
  occurredAt: string;
  description: string;
  code?: string | null;
}

export type UpdateMovementInput = Partial<CreateMovementInput>;

// J04 v1 — Certificados digitais
export interface OfficeCertificateView {
  id: string;
  subjectCn: string;
  issuerCn: string;
  serialNumber: string;
  notBefore: string;
  notAfter: string;
  holderDocument: string;
  holderName: string;
  status: 'active' | 'expired' | 'revoked' | 'removed';
  lastValidatedAt: string | null;
  createdAt: string;
  daysUntilExpiry: number;
  isExpired: boolean;
}

export interface UploadCertificateInput {
  filename: string;
  pfxBase64: string;
  password: string;
}

// ---------------------------------------------------------------------------
// J01/J08 — Polling DJEN + intimacoes
// ---------------------------------------------------------------------------
//
// office_oabs foi descomissionado na Rodada B do J08. Polling agora itera
// monitoring_subjects (ver bloco "J08.2 — Monitoramentos").

export interface DjenPollResultPerSource {
  sourceId: string;
  sourceKind: 'oab' | 'cpf' | 'cnpj' | 'nome_parte' | 'djen' | 'tribunal';
  value: string;
  /**
   * - 'monitoring'      : pollada com sucesso
   * - 'failure'         : erro real (HTTP, timeout, configuracao invalida)
   * - 'pending'         : agregadora; polling roda via OAB/nome do subject
   * - 'no_certificate'  : tribunal aguardando cert A1 (J05)
   */
  status: 'monitoring' | 'failure' | 'pending' | 'no_certificate';
  statusReason: string | null;
  importadas: number;
  duplicadas: number;
  vinculadasACase: number;
}

export interface DjenPollResultResponse {
  subjectId: string;
  displayName: string;
  kindSubject: 'pf' | 'pj';
  sources: DjenPollResultPerSource[];
  importadas: number;
  duplicadas: number;
  vinculadasACase: number;
  janelaDias: number;
}

export interface IntimacaoTermMatchView {
  termId: string;
  term: string;
  category: string | null;
  matchType: string;
  position: number | null;
  text: string | null;
}

// J09 — Parser de publicacoes
export type ParsedActType =
  | 'sentenca'
  | 'despacho'
  | 'decisao'
  | 'intimacao'
  | 'citacao'
  | 'audiencia'
  | 'recurso'
  | 'contestacao'
  | 'manifestacao'
  | 'outro';

export type ParsedDeadlineUnit = 'dias_uteis' | 'dias_corridos';

export interface ParsedPublicationSummary {
  actType: ParsedActType | null;
  hasDeadline: boolean;
  deadlineDays: number | null;
  deadlineUnit: ParsedDeadlineUnit | null;
  audienceDate: string | null;
  confidence: 'high' | 'medium' | 'low';
  /** Primeiro nome classificado como polo ativo pelo parser J09. */
  poloAtivo: string | null;
  /** Primeiro nome classificado como polo passivo pelo parser J09. */
  poloPassivo: string | null;
}

export interface ParsedHighlightRange {
  start: number;
  end: number;
  label: string;
}

export interface ParsedPublication extends ParsedPublicationSummary {
  audienceDate: string | null;
  judgeName: string | null;
  mentionedParties: string[];
  cnjMentioned: string | null;
  rawHighlights: ParsedHighlightRange[];
}

// J07 — Regras de prazo
export type DeadlineActType = ParsedActType;
export type DeadlineUnit = ParsedDeadlineUnit;

export interface DeadlineRule {
  id: string;
  tenantId: string;
  actType: DeadlineActType;
  defaultDays: number;
  defaultUnit: DeadlineUnit;
  notes: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateDeadlineRuleInput {
  actType: DeadlineActType;
  defaultDays: number;
  defaultUnit: DeadlineUnit;
  notes?: string | null;
}

export interface UpdateDeadlineRuleInput {
  defaultDays?: number;
  defaultUnit?: DeadlineUnit;
  notes?: string | null;
}

export interface Intimacao {
  id: string;
  tenantId: string;
  idDjen: number;
  numeroComunicacao: string;
  hash: string;
  numeroProcesso: string;
  numeroProcessoMascara: string | null;
  dataDisponibilizacao: string;
  siglaTribunal: string;
  tipoComunicacao: string | null;
  nomeOrgao: string | null;
  nomeClasse: string | null;
  texto: string;
  linkInteiroTeor: string | null;
  meio: 'D' | 'E' | null;
  destinatarios: unknown;
  destinatarioAdvogados: unknown;
  /** Sujeito monitorado que originou a intimacao (J08 Rodada B). */
  monitoringSubjectId: string | null;
  /** Display do subject vinculado (preenchido em list/get). */
  subjectDisplayName?: string | null;
  subjectKind?: 'pf' | 'pj' | null;
  caseId: string | null;
  lidoEm: string | null;
  prazoDiasUteis: number | null;
  prazoDataLimite: string | null;
  /** Timestamp em que a task foi gerada automaticamente pelo scheduler J07. */
  taskGeneratedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  oabNumero: string | null;
  oabUf: string | null;
  caseNumeroCnj: string | null;
  /** J08 — populado na lista. */
  monitoredTermIds?: string[];
  /** J08 — populado em getById/markAsRead/linkCase. */
  matches?: IntimacaoTermMatchView[];
  /** J09 — summary na lista, completo no detalhe. */
  parsed?: ParsedPublicationSummary | ParsedPublication;
}

// J08 v1 — Termos monitorados
export type MonitoredTermMatchType = 'word' | 'phrase' | 'regex';

export interface MonitoredTerm {
  id: string;
  tenantId: string;
  term: string;
  matchType: MonitoredTermMatchType;
  caseSensitive: boolean;
  category: string | null;
  description: string | null;
  active: boolean;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  matchesCount: number;
  lastMatchedAt: string | null;
}

export interface CreateMonitoredTermInput {
  term: string;
  matchType?: MonitoredTermMatchType;
  caseSensitive?: boolean;
  category?: string | null;
  description?: string | null;
  active?: boolean;
}

export type UpdateMonitoredTermInput = Partial<CreateMonitoredTermInput>;

export interface RescanResultResponse {
  termId: string;
  scanned: number;
  newMatches: number;
}

// ---------------------------------------------------------------------------
// Customizacoes — solicitacoes de customizacao por escritorio
// ---------------------------------------------------------------------------

export type CustomizationRequestStatus =
  | 'pending'
  | 'reviewing'
  | 'quoted'
  | 'in_progress'
  | 'done'
  | 'rejected';

export interface CustomizationRequest {
  id: string;
  tenantId: string;
  requestedByUserId: string;
  title: string;
  description: string;
  status: CustomizationRequestStatus;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateCustomizationRequestInput {
  title: string;
  description: string;
}

// ---------------------------------------------------------------------------
// J08.2 — Monitoramentos (sujeitos PF/PJ + sources)
// ---------------------------------------------------------------------------

/**
 * Backend so persiste 'pf' | 'pj'. 'lawyer' e um pseudo-kind usado na UI
 * para indicar "PF com OAB" (vira PF no payload enviado ao backend).
 */
export type MonitoringSubjectKind = 'lawyer' | 'pf' | 'pj';
export type MonitoringSourceKind =
  | 'oab'
  | 'cpf'
  | 'cnpj'
  | 'nome_parte'
  | 'djen'
  | 'tribunal';

export type MonitoringSourceStatus =
  | 'pending'
  | 'monitoring'
  | 'failure'
  | 'no_certificate';

export interface MonitoringSource {
  id: string;
  tenantId: string;
  subjectId: string;
  kind: MonitoringSourceKind;
  value: string;
  tribunalCode: string | null;
  dependsOnCertificate: boolean;
  active: boolean;
  lastPolledAt: string | null;
  status: MonitoringSourceStatus;
  statusReason: string | null;
  errorMessage: string | null;
  lastCheckAt: string | null;
  lastSuccessAt: string | null;
  intimacaoCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface MonitoringSubject {
  id: string;
  tenantId: string;
  kind: MonitoringSubjectKind;
  displayName: string;
  cpf: string | null;
  cnpj: string | null;
  razaoSocial: string | null;
  oabNumero: string | null;
  oabUf: string | null;
  monitorDirectOabOrDoc: boolean;
  monitorTribunaisSuperiores: boolean;
  monitoredStates: string[];
  certificateId: string | null;
  description: string | null;
  active: boolean;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  sources: MonitoringSource[];
}

export interface CreateMonitoringSubjectInput {
  kind: MonitoringSubjectKind;
  displayName: string;
  cpf?: string | null;
  cnpj?: string | null;
  razaoSocial?: string | null;
  oabNumero?: string | null;
  oabUf?: string | null;
  monitorDirectOabOrDoc?: boolean;
  monitorTribunaisSuperiores?: boolean;
  monitoredStates?: string[];
  certificateId?: string | null;
  description?: string | null;
  active?: boolean;
}

export type UpdateMonitoringSubjectInput = Partial<
  Omit<CreateMonitoringSubjectInput, 'kind'>
>;

export interface ListMonitoringSubjectsParams {
  active?: 'true' | 'false' | 'all';
  kind?: MonitoringSubjectKind | 'all';
  search?: string;
}

export interface IntimacoesListResponse {
  items: Intimacao[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface IntimacoesSummaryResponse {
  totalNotRead: number;
  last7Days: number;
}

export type IntimacoesLidoFilter = 'null' | 'notnull' | 'all';

export interface ListIntimacoesParams {
  lidoEm?: IntimacoesLidoFilter;
  caseId?: string;
  oabId?: string;
  dataInicio?: string;
  dataFim?: string;
  tribunal?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}



// ---------------------------------------------------------------------------
// F05 — Notifications
// ---------------------------------------------------------------------------

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export type NotificationTemplateKey =
  | 'intimacao.new'
  | 'monitored_term.matched'
  | 'deadline.approaching'
  | 'invite.accepted'
  | 'client.new'
  | (string & {});

export interface Notification {
  id: string;
  templateKey: NotificationTemplateKey;
  title: string;
  body: string;
  link: string | null;
  priority: NotificationPriority;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsListResponse {
  items: Notification[];
  total: number;
}

export interface NotificationsUnreadCountResponse {
  count: number;
}

export interface NotificationPreference {
  templateKey: NotificationTemplateKey;
  inAppEnabled: boolean;
  emailEnabled: boolean;
}

export interface NotificationPreferencesListResponse {
  items: NotificationPreference[];
}

export interface NotificationTemplateCatalogItem {
  templateKey: NotificationTemplateKey;
  label: string;
  description: string;
  defaultPriority: string;
}

export interface NotificationTemplatesCatalogResponse {
  items: NotificationTemplateCatalogItem[];
}

// D03 Tasks
export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'canceled';
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';
export type TaskSource = 'manual' | 'workflow' | 'ia' | 'intimation';
export type TaskAssigneeRole = 'responsible' | 'observer';

export interface TaskCategory {
  id: string;
  tenantId: string;
  name: string;
  color: string;
  icon: string | null;
  orderIndex: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface TaskAssigneeView {
  id: string;
  userId: string;
  name: string;
  role: TaskAssigneeRole;
}

export interface TaskListItem {
  id: string;
  tenantId: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  caseId: string | null;
  clientId: string | null;
  intimacaoId: string | null;
  processReference: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  fatalDate: string | null;
  completedAt: string | null;
  completedByUserId: string | null;
  pinned: boolean;
  source: TaskSource;
  parentTaskId: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  category: { id: string; name: string; color: string } | null;
  case: { id: string; numeroCnj: string | null } | null;
  client: { id: string; name: string } | null;
  intimacao: {
    id: string;
    numeroProcesso: string;
    numeroProcessoMascara: string | null;
  } | null;
  assignees: TaskAssigneeView[];
  // Campos D06 (agenda)
  dueTime: string | null;
  allDay: boolean;
  endAt: string | null;
  location: string | null;
  showInAgenda: boolean;
  isImportant: boolean;
  isUrgent: boolean;
  isPrivate: boolean;
  isRecurring: boolean;
  /** RRULE string (so presente nas tasks master) */
  recurrenceRule: string | null;
  recurrenceParentId: string | null;
  recurrenceOverrideDate: string | null;
  isFuture: boolean;
  isRetroactive: boolean;
}

export interface TaskCommentAttachment {
  id: string;
  commentId: string;
  storageObjectId: string;
  uploadedByUserId: string | null;
  createdAt: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface TaskComment {
  id: string;
  taskId: string;
  userId: string | null;
  userName: string | null;
  content: string;
  parentCommentId: string | null;
  isPrivate: boolean;
  editedAt: string | null;
  createdAt: string;
  attachments: TaskCommentAttachment[];
}

export interface TaskAttachment {
  id: string;
  taskId: string;
  storageObjectId: string;
  uploadedByUserId: string;
  createdAt: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface TaskDetail extends TaskListItem {
  comments: TaskComment[];
  attachments: TaskAttachment[];
}

export interface TaskHistoryEntry {
  id: string;
  action: string;
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface TaskEmailSend {
  id: string;
  taskId: string;
  sentByUserId: string | null;
  toEmail: string;
  subject: string;
  body: string;
  status: string;
  errorMessage: string | null;
  sentAt: string;
}

export interface TasksListResponse {
  items: TaskListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TaskStatsSummary {
  totalPending: number;
  totalOverdue: number;
  completedThisMonth: number;
}

export interface CreateTaskInput {
  title: string;
  description?: string | null;
  categoryId?: string | null;
  caseId?: string | null;
  clientId?: string | null;
  intimacaoId?: string | null;
  processReference?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  fatalDate?: string | null;
  pinned?: boolean;
  parentTaskId?: string | null;
  assigneeUserIds?: string[];
  // Campos D06
  dueTime?: string | null;
  allDay?: boolean;
  endAt?: string | null;
  location?: string | null;
  showInAgenda?: boolean;
  isImportant?: boolean;
  isUrgent?: boolean;
  isPrivate?: boolean;
  isRecurring?: boolean;
  recurrenceRule?: string | null;
  isFuture?: boolean;
  isRetroactive?: boolean;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string | null;
  categoryId?: string | null;
  caseId?: string | null;
  clientId?: string | null;
  processReference?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  fatalDate?: string | null;
  pinned?: boolean;
  assigneeUserIds?: string[];
  // Campos D06
  dueTime?: string | null;
  allDay?: boolean;
  endAt?: string | null;
  location?: string | null;
  showInAgenda?: boolean;
  isImportant?: boolean;
  isUrgent?: boolean;
  isPrivate?: boolean;
  isRecurring?: boolean;
  recurrenceRule?: string | null;
  isFuture?: boolean;
  isRetroactive?: boolean;
}

export interface CreateTaskCommentInput {
  content: string;
  parentCommentId?: string | null;
  isPrivate?: boolean;
}

export interface UpdateTaskCommentInput {
  content?: string;
  isPrivate?: boolean;
}

export interface ListTasksParams {
  assigneeUserId?: 'me' | string;
  status?: TaskStatus;
  priority?: TaskPriority;
  categoryId?: string;
  caseId?: string;
  clientId?: string;
  pinned?: boolean;
  dueFrom?: string;
  dueTo?: string;
  fatalFrom?: string;
  fatalTo?: string;
  search?: string;
  orderBy?: 'fatalDate' | 'dueDate' | 'createdAt' | 'priority';
  orderDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// D09 Financeiro
// ---------------------------------------------------------------------------

export type BankAccountType =
  | 'conta_corrente'
  | 'conta_poupanca'
  | 'cartao_credito'
  | 'dinheiro'
  | 'asaas'
  | 'investimento'
  | 'outros';

export interface BankAccount {
  id: string;
  tenantId: string;
  name: string;
  type: BankAccountType;
  bankInstitution: string | null;
  initialBalance: string;
  isDefault: boolean;
  active: boolean;
  balance: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface FinancialCategory {
  id: string;
  tenantId: string;
  name: string;
  type: 'income' | 'expense';
  parentId: string | null;
  color: string;
  active: boolean;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface FinancialCategoryNode extends FinancialCategory {
  children: FinancialCategoryNode[];
}

export interface CostCenter {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type FinancialTransactionType = 'income' | 'expense' | 'transfer';
export type FinancialTransactionStatus =
  | 'pending'
  | 'paid'
  | 'overdue'
  | 'canceled';

export interface FinancialTransactionListItem {
  id: string;
  tenantId: string;
  type: FinancialTransactionType;
  amount: string;
  description: string;
  dueDate: string;
  paymentDate: string | null;
  status: FinancialTransactionStatus;
  accountId: string;
  categoryId: string | null;
  costCenterId: string;
  clientId: string | null;
  caseId: string | null;
  competenceMonth: string | null;
  isInternalOnly: boolean;
  contractId: string | null;
  contractInstallmentSeq: number | null;
  transferTargetTransactionId: string | null;
  notes: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  account: { id: string; name: string; type: string } | null;
  category: { id: string; name: string; color: string; type: string } | null;
  costCenter: { id: string; name: string } | null;
  client: { id: string; name: string } | null;
  case: { id: string; numeroCnj: string | null } | null;
}

export interface FinancialAttachment {
  id: string;
  transactionId: string;
  storageObjectId: string;
  uploadedByUserId: string;
  createdAt: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface FinancialTransactionDetail
  extends FinancialTransactionListItem {
  attachments: FinancialAttachment[];
}

export interface FinancialTransactionsListResponse {
  items: FinancialTransactionListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface FinancialTransactionsTotalsResponse {
  totalIncome: number;
  totalExpense: number;
  balance: number;
  count: number;
}

/** Escopos de exclusao em lote de uma serie (contrato/parcelamento/recorrencia). */
export type FinancialTransactionDeleteScope = 'this_and_next' | 'all';

export interface FinancialTransactionDeleteScopeCounts {
  count: number;
  paidCount: number;
}

export interface FinancialTransactionDeletePreview {
  hasSeries: boolean;
  contractId: string | null;
  onlyThis: FinancialTransactionDeleteScopeCounts;
  thisAndNext: FinancialTransactionDeleteScopeCounts;
  all: FinancialTransactionDeleteScopeCounts;
}

export interface FinancialTransactionDeleteBatchResult {
  deletedIds: string[];
  deletedCount: number;
  contractId: string;
  seriesEmptied: boolean;
}

export interface ListTransactionsParams {
  type?: FinancialTransactionType;
  status?: FinancialTransactionStatus;
  accountId?: string;
  categoryId?: string;
  /** Multi-selecao em arvore (ver category-filter.tsx) — pai ja traz as subcategorias, expandido no backend. */
  categoryIds?: string[];
  costCenterId?: string;
  clientId?: string;
  caseId?: string;
  contractId?: string;
  dueFrom?: string;
  dueTo?: string;
  paymentFrom?: string;
  paymentTo?: string;
  search?: string;
  orderBy?: 'dueDate' | 'paymentDate' | 'createdAt' | 'amount' | 'category';
  orderDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface CreateTransactionInput {
  type: 'income' | 'expense';
  amount: number;
  description: string;
  dueDate: string;
  paymentDate?: string | null;
  accountId: string;
  categoryId?: string | null;
  costCenterId: string;
  clientId?: string | null;
  caseId?: string | null;
  competenceMonth?: string | null;
  isInternalOnly?: boolean;
  notes?: string | null;
  recurrence?: {
    modality: 'recurring' | 'installment';
    installmentsCount?: number;
    totalAmount?: number;
    frequencyMonths?: number;
    contractName?: string;
    // Parcelas customizadas: valor e vencimento explicitos por parcela.
    installments?: Array<{ amount: number; dueDate: string }>;
  };
}

export interface UpdateTransactionInput {
  amount?: number;
  description?: string;
  dueDate?: string;
  paymentDate?: string | null;
  accountId?: string;
  categoryId?: string | null;
  costCenterId?: string;
  clientId?: string | null;
  caseId?: string | null;
  competenceMonth?: string | null;
  isInternalOnly?: boolean;
  notes?: string | null;
  status?: FinancialTransactionStatus;
}

export interface TransferInput {
  fromAccountId: string;
  toAccountId: string;
  amount: number;
  dueDate: string;
  paymentDate?: string | null;
  description: string;
  costCenterId: string;
  notes?: string | null;
}

export interface FinancialContract {
  id: string;
  tenantId: string;
  name: string;
  type: 'income' | 'expense';
  modality: 'recurring' | 'installment';
  totalAmount: string | null;
  installmentAmount: string | null;
  installmentsCount: number | null;
  frequencyMonths: number;
  firstDueDate: string;
  accountId: string;
  categoryId: string | null;
  costCenterId: string;
  clientId: string | null;
  caseId: string | null;
  status: 'active' | 'paused' | 'canceled' | 'completed' | 'renegotiated';
  /** Aponta para o contrato ORIGINAL quando este nasceu de uma renegociacao. */
  renegotiatedFromContractId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RenegotiateContractInput {
  description?: string;
  totalAmount: number;
  installmentsCount: number;
  firstDueDate: string;
  frequencyMonths?: number;
  installments?: Array<{ amount: number; dueDate: string }>;
  accountId?: string;
  categoryId?: string | null;
  costCenterId?: string;
  clientId?: string | null;
  caseId?: string | null;
  contractName?: string;
}

export interface RenegotiateContractResult {
  /** Contrato original, agora com status='renegotiated'. */
  originalContract: FinancialContract;
  /** Contrato novo, com renegotiatedFromContractId apontando pro original. */
  newContract: FinancialContract;
  firstTransactionId: string | null;
  /** Quantas parcelas em aberto (pending/overdue) do original foram fechadas. */
  closedInstallmentCount: number;
}

export interface FinancialContractDetail {
  contract: FinancialContract;
  stats: {
    installmentsTotal: number;
    installmentsPaid: number;
    amountPaid: number;
    amountPending: number;
  };
  /** Id do lancamento que substituiu este, se este foi renegociado. */
  renegotiatedToContractId: string | null;
  /** Nome do lancamento acima, para o link nao precisar de outro fetch. */
  renegotiatedToContractName: string | null;
}

export type ImportGroupingSource = 'note' | 'description';

export interface ImportGroupingInstallment {
  transactionId: string;
  seq: number;
  totalSeq: number;
  dueDate: string;
  amount: string;
  status: string;
  description: string;
  isDuplicate: boolean;
}

export interface ImportGroupingSignals {
  denominator: number | null;
  presentSeqs: number[];
  missingSeqs: number[];
  duplicateSeqs: number[];
  amountVaries: boolean;
  distinctAmounts: string[];
  cadenceMonthly: boolean;
}

export interface ImportGroupingSuggestion {
  groupKey: string;
  source: ImportGroupingSource;
  sampleDescription: string;
  clientId: string | null;
  clientName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  totalAmount: number;
  installments: ImportGroupingInstallment[];
  suggestedContractName: string;
  signals: ImportGroupingSignals;
}

export interface ImportGroupingsSuggestionsResponse {
  items: ImportGroupingSuggestion[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface FinancialStatsSummary {
  saldoAtual: number;
  receitaMensalPrevista: number;
  receitaMensalRealizada: number;
  despesaMensalPrevista: number;
  despesaMensalRealizada: number;
  pagamentosAtrasados: { total: number; count: number };
  contas: Array<{
    accountId: string;
    name: string;
    balance: number;
    type: string;
  }>;
}

export interface FinancialChartPoint {
  month: string;
  incomePrevista: number;
  incomeRealizada: number;
  expensePrevista: number;
  expenseRealizada: number;
}

export interface FinancialTopCategory {
  categoryId: string | null;
  name: string;
  color: string;
  total: number;
}

// ---------------------------------------------------------------------------
// D07 Documentos / Templates (fase A2)
// ---------------------------------------------------------------------------

export type DocumentTemplateStatus = 'active' | 'archived';
export type DocumentTemplateArea =
  | 'civel'
  | 'trabalhista'
  | 'criminal'
  | 'tributario'
  | 'outros';
export type DocumentTemplateSubtype = 'simples' | 'complexo';

export interface ReportConfig {
  baseEntity: 'process' | 'client';
  sections?: {
    movimentacoes?: boolean;
    documentos?: boolean;
    partes?: boolean;
    financeiro?: boolean;
  };
  introText?: string;
  showOfficeHeader?: boolean;
}

export interface DocumentTemplate {
  id: string;
  tenantId: string;
  categoryId: string | null;
  name: string;
  description: string | null;
  contentJson: unknown;
  reportConfig: ReportConfig | null;
  area: DocumentTemplateArea | null;
  subtype: DocumentTemplateSubtype | null;
  status: DocumentTemplateStatus;
  autoGenerateOnClient: boolean;
  autoGenerateOnProcess: boolean;
  showHeader: boolean;
  showFooter: boolean;
  headerContentJson: unknown | null;
  footerContentJson: unknown | null;
  version: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AdvboxImportResult {
  template: DocumentTemplate;
  stats: {
    variablesDetected: number;
    unmappedTokens: string[];
  };
}

export interface DocumentTemplateCategoryNode {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  description: string | null;
  sortOrder: number;
  children: DocumentTemplateCategoryNode[];
}

export interface DocumentTemplateCategory {
  id: string;
  tenantId: string;
  parentId: string | null;
  name: string;
  slug: string;
  description: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateDocumentTemplateCategoryInput {
  name: string;
  parentId?: string | null;
  description?: string | null;
  sortOrder?: number;
}

export type UpdateDocumentTemplateCategoryInput = Partial<CreateDocumentTemplateCategoryInput>;

export interface ListDocumentTemplatesQuery {
  categoryId?: string;
  area?: DocumentTemplateArea;
  status?: DocumentTemplateStatus;
  search?: string;
}

export interface CreateDocumentTemplateInput {
  name: string;
  description?: string | null;
  categoryId?: string | null;
  area?: DocumentTemplateArea | null;
  subtype?: DocumentTemplateSubtype | null;
  status?: DocumentTemplateStatus;
  autoGenerateOnClient?: boolean;
  autoGenerateOnProcess?: boolean;
  contentJson?: unknown;
  showHeader?: boolean;
  showFooter?: boolean;
  headerContentJson?: unknown | null;
  footerContentJson?: unknown | null;
}

export interface UpdateDocumentTemplateInput {
  name?: string;
  description?: string | null;
  categoryId?: string | null;
  area?: DocumentTemplateArea | null;
  subtype?: DocumentTemplateSubtype | null;
  status?: DocumentTemplateStatus;
  autoGenerateOnClient?: boolean;
  autoGenerateOnProcess?: boolean;
  contentJson?: unknown;
  showHeader?: boolean;
  showFooter?: boolean;
  headerContentJson?: unknown | null;
  footerContentJson?: unknown | null;
}

export type DocumentTemplateVariableSource =
  | 'client'
  | 'process'
  | 'office'
  | 'user'
  | 'system';

export type DocumentTemplateVariableDataType =
  | 'string'
  | 'date'
  | 'number'
  | 'boolean';

export interface DocumentTemplateVariable {
  id: string;
  tenantId: string | null;
  key: string;
  label: string;
  source: DocumentTemplateVariableSource;
  dataType: DocumentTemplateVariableDataType;
  defaultFormat: string | null;
  requiredContext: string[] | null;
  isGlobal: boolean;
  /** Variavel de bloco: ocupa linha inteira; usada em modelos de relatorio. */
  isBlock?: boolean;
}

// ---------------------------------------------------------------------------
// D07 Documentos / Instances (fase A5)
// ---------------------------------------------------------------------------

export type DocumentInstanceStatus =
  | 'draft'
  | 'finalized'
  | 'signed'
  | 'archived';

// -------------------------------------------------------------------------
// D06 Agenda
// -------------------------------------------------------------------------

export interface AgendaEvent {
  id: string;
  taskId: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  location: string | null;
  status: 'pending' | 'completed' | 'overdue';
  isImportant: boolean;
  isUrgent: boolean;
  isPrivate: boolean;
  isRecurring: boolean;
  categoryId: string | null;
  categoryColor: string | null;
  categoryLabel: string | null;
  responsibles: Array<{ userId: string; name: string }>;
  caseId: string | null;
  clientId: string | null;
  /** true quando e uma ocorrencia virtual expandida da RRULE */
  isOccurrence: boolean;
  /** data ISO da ocorrencia (so quando isOccurrence=true) */
  occurrenceDate: string | null;
  /** id da task master (so quando isOccurrence=true ou e override) */
  masterTaskId: string | null;
}

export interface CreateOccurrenceOverrideInput {
  title?: string;
  description?: string | null;
  categoryId?: string | null;
  dueDate?: string | null;
  dueTime?: string | null;
  allDay?: boolean;
  endAt?: string | null;
  location?: string | null;
  isImportant?: boolean;
  isUrgent?: boolean;
  isPrivate?: boolean;
  status?: string;
  priority?: string;
  fatalDate?: string | null;
  assigneeUserIds?: string[];
}

export interface DocumentInstance {
  id: string;
  tenantId: string;
  templateId: string | null;
  templateVersion: number | null;
  templateNameSnapshot: string | null;
  clientId: string | null;
  caseId: string | null;
  title: string;
  contentJson: unknown;
  status: DocumentInstanceStatus;
  showHeader: boolean;
  showFooter: boolean;
  headerContentJson: unknown | null;
  footerContentJson: unknown | null;
  pdfStorageObjectId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateDocumentInstanceInput {
  templateId: string;
  clientId?: string | null;
  caseId?: string | null;
  title?: string | null;
  showHeader?: boolean;
  showFooter?: boolean;
  headerContentJson?: unknown | null;
  footerContentJson?: unknown | null;
}

export interface UpdateDocumentInstanceInput {
  title?: string | null;
  contentJson?: unknown;
  status?: DocumentInstanceStatus;
  clientId?: string | null;
  caseId?: string | null;
  showHeader?: boolean;
  showFooter?: boolean;
  headerContentJson?: unknown | null;
  footerContentJson?: unknown | null;
}

export interface ListDocumentInstancesQuery {
  clientId?: string;
  caseId?: string;
  templateId?: string;
  status?: DocumentInstanceStatus;
  search?: string;
}

// ---------------------------------------------------------------------------
// Reports — Automacoes e Entregas
// ---------------------------------------------------------------------------

export type ReportAutomationTriggerType = 'case_closed' | 'schedule_monthly' | 'manual';
export type ReportAutomationRecipientRule = 'case_main_client' | 'responsible_user' | 'custom_email';
export type ReportAutomationChannel = 'email' | 'in_app';

export interface ReportAutomation {
  id: string;
  tenantId: string;
  name: string;
  reportTemplateId: string;
  reportTemplateName?: string | null;
  triggerType: ReportAutomationTriggerType;
  recipientRule: ReportAutomationRecipientRule;
  recipientCustomEmail: string | null;
  channel: ReportAutomationChannel;
  requiresApproval: boolean;
  enabled: boolean;
  filters: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReportAutomationInput {
  name: string;
  reportTemplateId: string;
  triggerType: ReportAutomationTriggerType;
  recipientRule: ReportAutomationRecipientRule;
  recipientCustomEmail?: string | null;
  channel?: ReportAutomationChannel;
  requiresApproval?: boolean;
  enabled?: boolean;
  filters?: Record<string, unknown>;
}

export type UpdateReportAutomationInput = Partial<CreateReportAutomationInput>;

export type ReportDeliveryStatus =
  | 'pending_approval'
  | 'approved'
  | 'sending'
  | 'sent'
  | 'failed'
  | 'rejected'
  | 'skipped';

export interface ReportDelivery {
  id: string;
  tenantId: string;
  status: ReportDeliveryStatus;
  triggerType: ReportAutomationTriggerType;
  channel: ReportAutomationChannel;
  recipientEmail: string | null;
  caseId: string | null;
  documentInstanceId: string | null;
  sentAt: string | null;
  error: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Reports — Analytics Overview
// ---------------------------------------------------------------------------

export interface ReportsAnalyticsOverview {
  kpis: {
    activeCases: number;
    activeClients: number;
    periodRevenue: number;
    periodExpense: number;
    periodBalance: number;
    openTasks: number;
    overdueTasks: number;
  };
  casesByStatus: Array<{ status: 'active' | 'archived' | 'suspended' | 'closed'; count: number }>;
  casesByGroup: Array<{ label: string; count: number }>;
  monthlyFinance: Array<{ month: string; revenue: number; expense: number }>;
  newClientsMonthly: Array<{ month: string; count: number }>;
  tasksByResponsible: Array<{ userName: string; completed: number }>;
}

// ---------------------------------------------------------------------------
// Busca global
// ---------------------------------------------------------------------------

export interface ClientSearchHit {
  id: string;
  name: string;
  document: string | null;
  email: string | null;
  phone: string | null;
  kind: 'pf' | 'pj';
}

export interface CaseSearchHit {
  id: string;
  cnjNumber: string | null;
  actionType: string | null;
  clientName: string | null;
  tribunal: string | null;
}

export interface DocumentSearchHit {
  id: string;
  title: string;
  clientName: string | null;
  createdAt: string;
}

export interface TaskSearchHit {
  id: string;
  title: string;
  status: string;
  dueDate: string | null;
}

export interface SearchResults {
  clients: ClientSearchHit[];
  cases: CaseSearchHit[];
  documents: DocumentSearchHit[];
  tasks: TaskSearchHit[];
  total: number;
}

// ---------------------------------------------------------------------------
// Assistente de IA (ADR-0015) — confirmacao de acao proposta (human-in-the-loop)
// ---------------------------------------------------------------------------

/**
 * Candidato de um card de desambiguacao de cliente (select_client). Emitido
 * pela tool quando o nome informado bate com mais de um cliente do tenant.
 */
export interface AssistantClientSelectionCandidate {
  id: string;
  name: string;
  type: ClientType;
  document: string | null;
  phone: string | null;
  email: string | null;
  /** Numeros CNJ de ate 3 processos vinculados ao cliente. */
  cases: string[];
}

/**
 * Resultado da tool select_client quando ha mais de um cliente com nome
 * ambiguo. O front renderiza um card por candidato (ClientSelectionCard); ao
 * clicar, reinjeta a escolha no chat como uma nova mensagem do usuario.
 */
export interface AssistantClientSelection {
  type: 'client_selection';
  candidates: AssistantClientSelectionCandidate[];
}

/**
 * Candidato de um card de desambiguacao de PROCESSO (select_case), filtrado
 * por CLIENTE — espelha o filtro em cascata da tela manual de geracao de
 * documento (primeiro cliente, depois processo). Emitido quando o cliente
 * tem mais de um processo.
 */
export interface AssistantCaseSelectionCandidate {
  id: string;
  numeroCnj: string | null;
  actionTypeName: string | null;
  tribunalCode: string | null;
  vara: string | null;
  status: CaseStatus;
}

/**
 * Resultado da tool select_case quando o cliente tem mais de um processo. O
 * front renderiza um card por candidato (CaseSelectionCard); ao clicar,
 * reinjeta a escolha no chat como uma nova mensagem do usuario — mesmo
 * mecanismo do client_selection (o modelo precisa do caseId para seguir o
 * fluxo, ex: gerar documento).
 */
export interface AssistantCaseSelection {
  type: 'case_selection';
  candidates: AssistantCaseSelectionCandidate[];
  totalFound: number;
}

/**
 * Candidato de um card de selecao de lancamento (select_transaction). Emitido
 * pela tool ao localizar lancamentos para o usuario ESCOLHER qual excluir.
 */
export interface AssistantTransactionSelectionCandidate {
  id: string;
  description: string;
  /** Valor em reais, ja normalizado. O card formata em BRL. */
  amount: number;
  type: 'income' | 'expense' | 'transfer';
  dueDate: string;
  /** Data/hora de CRIACAO do lancamento (America/Sao_Paulo), nao o vencimento. */
  createdAt: string;
  status: string;
  clientName: string | null;
}

/**
 * Resultado da tool select_transaction. SEMPRE presente quando ha ao menos 1
 * candidato (diferente de select_client) — e acao DESTRUTIVA, entao o
 * usuario sempre confirma clicando, mesmo com 1 unico resultado. Ao clicar,
 * o front chama POST /assistant/actions/financial-delete-proposal com o id
 * do candidato DIRETO (assistantFinancialDeleteProposal) — nunca reinjeta no
 * chat, ao contrario de client_selection: o id nunca deve voltar a passar
 * pelo modelo.
 */
export interface AssistantTransactionSelection {
  type: 'transaction_selection';
  candidates: AssistantTransactionSelectionCandidate[];
}

/**
 * Candidato de um card de selecao de MODELO de documento
 * (select_document_template). Emitido quando mais de um modelo bate com o que
 * o usuario pediu.
 */
export interface AssistantDocumentTemplateCandidate {
  id: string;
  name: string;
  categoryName: string | null;
  description: string | null;
}

/**
 * Resultado da tool select_document_template com mais de um modelo compativel.
 * Com um unico modelo a tool devolve o modelo direto (sem este envelope).
 * Ao clicar, o front reinjeta a escolha no chat como mensagem do usuario —
 * padrao do client_selection, nao do transaction_selection: aqui o modelo
 * PRECISA saber qual modelo foi escolhido para seguir o fluxo (inspecionar
 * variaveis, perguntar o que falta, propor a geracao).
 */
export interface AssistantDocumentTemplateSelection {
  type: 'document_template_selection';
  candidates: AssistantDocumentTemplateCandidate[];
  totalFound: number;
}

/**
 * Preview de uma proposta de cadastro de cliente, emitido pela tool
 * create_client no stream do chat. O card de confirmacao renderiza isto; ao
 * confirmar, chama assistantConfirmAction(proposalId).
 */
export interface AssistantCreateClientPreview {
  type: ClientType;
  name: string;
  document: string | null;
  status: string;
  primaryContact: { type: string; value: string } | null;
  contactsCount: number;
}

/** Preview de um lancamento financeiro proposto (create_financial_entry). */
export interface AssistantFinancialEntryPreview {
  type: 'income' | 'expense';
  /** Valor em reais, ja normalizado. O card formata em BRL. */
  amount: number;
  dueDate: string;
  paymentDate: string | null;
  description: string;
  accountName: string;
  costCenterName: string;
  categoryName: string | null;
  hasClient: boolean;
  hasCase: boolean;
}

/** Preview de uma categoria financeira proposta (create_financial_category). */
export interface AssistantFinancialCategoryPreview {
  name: string;
  type: 'income' | 'expense';
  color: string | null;
}

/**
 * Preview de uma EXCLUSAO de lancamento financeiro proposta
 * (delete_financial_entry). ACAO DESTRUTIVA — o card renderiza isto em tom de
 * alerta, nunca igual as propostas de criacao.
 */
export interface AssistantDeleteFinancialEntryPreview {
  type: 'income' | 'expense' | 'transfer';
  /** Valor em reais, ja normalizado. O card formata em BRL. */
  amount: number;
  description: string;
  dueDate: string;
  status: string;
  clientName: string | null;
  /** true quando o lancamento e uma ponta de transferencia: excluir apaga as DUAS. */
  isTransfer: boolean;
}

/**
 * Preview de uma GERACAO de documento proposta (generate_document). O card
 * mostra modelo, cliente/processo e quantas variaveis ficarao em branco (___)
 * antes do usuario confirmar.
 */
export interface AssistantGenerateDocumentPreview {
  templateName: string;
  clientName: string | null;
  caseNumber: string | null;
  totalVariables: number;
  filledCount: number;
  missingCount: number;
  /** Labels das variaveis que ficarao em branco (ate 8; o resto vira "+N"). */
  missingLabels: string[];
}

/**
 * Preview de um MODELO de documento redigido pela IA (create_document_template,
 * D08). O card mostra um RESUMO — nome, categoria, quantos paragrafos, quais
 * variaveis e um trecho do inicio — nunca o modelo inteiro: o texto pode ter
 * varias paginas e o lugar de ler/ajustar e o editor.
 */
export interface AssistantCreateDocumentTemplatePreview {
  name: string;
  categoryName: string | null;
  paragraphCount: number;
  variableCount: number;
  /** Rotulos das variaveis usadas (ate 10; o resto vira "+N"). */
  variableLabels: string[];
  /** Comeco do texto redigido, para o usuario reconhecer a peca. */
  excerpt: string;
}

/**
 * Preview de um processo proposto (create_case, D02). `dataJudFound` indica
 * se tribunal/vara/data/valor vieram do DataJud (true) ou foram informados
 * manualmente pelo usuario porque o DataJud nao encontrou o processo (false)
 * — o card sinaliza essa diferenca para o usuario conferir antes de gravar.
 */
export interface AssistantCreateCasePreview {
  numeroCnj: string;
  numeroCnjMascara: string;
  clientName: string;
  clientRole: string;
  actionGroupName: string | null;
  actionTypeName: string;
  nature: string;
  status: string;
  tribunalCode: string | null;
  vara: string | null;
  openedAt: string | null;
  valorCausa: number | null;
  dataJudFound: boolean;
  dataJudClasse: string | null;
  dataJudAssunto: string | null;
}

/**
 * Preview de uma tarefa/prazo proposta (create_task, D03). `dueDate`/
 * `fatalDate` ja chegam como data ABSOLUTA (YYYY-MM-DD) — a conversao de
 * expressao relativa ("sexta", "amanha") para data e feita pelo modelo antes
 * de propor, para o usuario confirmar o dia certo no card.
 */
export interface AssistantCreateTaskPreview {
  title: string;
  description: string | null;
  dueDate: string | null;
  dueTime: string | null;
  fatalDate: string | null;
  priority: string;
  categoryName: string | null;
  clientName: string | null;
  caseNumber: string | null;
  assigneeName: string;
  isImportant: boolean;
  isUrgent: boolean;
}

/**
 * Acao proposta por uma tool de escrita do assistente, discriminada por
 * `action`. Cada kind carrega seu proprio `preview`. Para adicionar um kind
 * novo: some um membro a esta uniao (+ o branch no ActionPreviewCard).
 */
export type AssistantProposedAction =
  | {
      type: 'proposed_action';
      action: 'create_client';
      proposalId: string;
      preview: AssistantCreateClientPreview;
    }
  | {
      type: 'proposed_action';
      action: 'create_financial_entry';
      proposalId: string;
      preview: AssistantFinancialEntryPreview;
    }
  | {
      type: 'proposed_action';
      action: 'create_financial_category';
      proposalId: string;
      preview: AssistantFinancialCategoryPreview;
    }
  | {
      type: 'proposed_action';
      action: 'delete_financial_entry';
      proposalId: string;
      preview: AssistantDeleteFinancialEntryPreview;
    }
  | {
      type: 'proposed_action';
      action: 'generate_document';
      proposalId: string;
      preview: AssistantGenerateDocumentPreview;
    }
  | {
      type: 'proposed_action';
      action: 'create_document_template';
      proposalId: string;
      preview: AssistantCreateDocumentTemplatePreview;
    }
  | {
      type: 'proposed_action';
      action: 'create_case';
      proposalId: string;
      preview: AssistantCreateCasePreview;
    }
  | {
      type: 'proposed_action';
      action: 'create_task';
      proposalId: string;
      preview: AssistantCreateTaskPreview;
    };

/** Resultado de um lancamento financeiro confirmado. */
export interface AssistantFinancialEntryResult {
  id: string;
  type: 'income' | 'expense';
  amount: number;
  description: string;
  dueDate: string;
  status: string;
}

/** Resultado de uma categoria financeira confirmada. */
export interface AssistantFinancialCategoryResult {
  id: string;
  name: string;
  type: 'income' | 'expense';
}

/**
 * Resultado de um lancamento financeiro EXCLUIDO por confirmacao. Sem
 * `status`: deixou de fazer sentido para algo excluido.
 */
export interface AssistantDeletedFinancialEntryResult {
  id: string;
  type: 'income' | 'expense' | 'transfer';
  amount: number;
  description: string;
  dueDate: string;
}

/**
 * Resultado de um documento GERADO por confirmacao. `editorPath` vem pronto do
 * backend (rota da SPA para o editor da instancia) — e o link que o card
 * entrega ao usuario.
 */
export interface AssistantGeneratedDocumentResult {
  id: string;
  title: string;
  templateName: string | null;
  editorPath: string;
  missingCount: number;
}

/**
 * Resultado de um MODELO de documento confirmado (create_document_template,
 * D08). `editorPath` aponta para o editor de MODELO — rota diferente da do
 * editor de instancia devolvido por generate_document.
 */
export interface AssistantCreatedTemplateResult {
  id: string;
  name: string;
  categoryName: string | null;
  editorPath: string;
  variableCount: number;
}

/** Resultado de um processo confirmado (create_case, D02). */
export interface AssistantCaseResult {
  id: string;
  numeroCnj: string | null;
  actionTypeName: string | null;
  status: string;
}

/** Resultado de uma tarefa confirmada (create_task, D03). */
export interface AssistantTaskResult {
  id: string;
  title: string;
  dueDate: string | null;
  fatalDate: string | null;
  status: string;
}

/** Resposta do confirm, discriminada por `action` (um shape por kind). */
export type AssistantConfirmActionResponse =
  | { action: 'create_client'; client: ClientDetailResponse }
  | { action: 'create_financial_entry'; transaction: AssistantFinancialEntryResult }
  | { action: 'create_financial_category'; category: AssistantFinancialCategoryResult }
  | { action: 'delete_financial_entry'; transaction: AssistantDeletedFinancialEntryResult }
  | { action: 'generate_document'; document: AssistantGeneratedDocumentResult }
  | { action: 'create_document_template'; template: AssistantCreatedTemplateResult }
  | { action: 'create_case'; case: AssistantCaseResult }
  | { action: 'create_task'; task: AssistantTaskResult };

/**
 * Resposta de POST /assistant/actions/financial-delete-proposal: a proposta
 * de exclusao criada a partir do transactionId clicado pelo usuario num
 * candidato de select_transaction. O front usa isto para montar diretamente
 * um AssistantProposedAction (kind delete_financial_entry) e renderizar o
 * card de confirmacao destrutivo (ActionPreviewCard) ja existente.
 */
export interface AssistantFinancialDeleteProposalResponse {
  proposalId: string;
  preview: AssistantDeleteFinancialEntryPreview;
}

// ---------------------------------------------------------------------------
// Portal do Cliente — caixa de entrada de upload, lado ESCRITORIO
// (docs/PORTAL-CLIENTE.md secao 6, Marco 4). Espelha
// apps/api/src/modules/portal/dto/portal-upload.dto.ts.
// ---------------------------------------------------------------------------

export type PortalUploadStatusFilter = 'pending' | 'approved' | 'rejected' | 'all';

export interface PortalUploadInboxItem {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  clientId: string;
  clientName: string;
  caseId: string | null;
  caseNumeroCnj: string | null;
  clientNote: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  createdAt: string;
}

export interface PortalUploadsListResponse {
  items: PortalUploadInboxItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type PortalUploadApproveDestination = 'client_attachment' | 'case_attachment';

export interface ApprovePortalUploadInput {
  destination: PortalUploadApproveDestination;
  caseId?: string;
}

export const apiClient = {
  // Auth core
  me: () =>
    call<MeResponse>('/auth/me', {
      method: 'GET',
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  signIn: (input: { email: string; password: string }) =>
    call<SignInResponse>('/auth/sign-in', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  signUp: (input: SignUpInput) =>
    call<SignUpResponse>('/auth/sign-up', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  // MFA setup (autenticado)
  mfaSetup: () =>
    call<EnableMfaResponse>('/auth/mfa/enable', { method: 'POST' }),

  mfaVerifySetup: (code: string) =>
    call<void>('/auth/mfa/verify-setup', {
      method: 'POST',
      body: { code },
    }),

  signOut: () =>
    call<void>('/auth/sign-out', {
      method: 'POST',
      skipAuthRefresh: true,
    }),

  refresh: () =>
    call<RefreshResponse>('/auth/refresh', {
      method: 'POST',
      body: {},
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  verifyMfaLogin: (input: { challengeId: string; code: string }) =>
    call<VerifyMfaLoginResponse>('/auth/mfa/verify-login', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  switchTenant: (tenantId: string) =>
    call<SwitchTenantResponse>('/auth/switch-tenant', {
      method: 'POST',
      body: { tenantId },
    }),

  // First-access (publico)
  firstAccessPreview: (token: string) =>
    call<FirstAccessPreviewResponse>(
      `/auth/first-access/${encodeURIComponent(token)}/preview`,
      { skipAuthRefresh: true, includeAuth: false },
    ),

  firstAccessActivate: (token: string, password: string) =>
    call<FirstAccessActivateResponse>(
      `/auth/first-access/${encodeURIComponent(token)}/activate`,
      {
        method: 'POST',
        body: { password },
        skipAuthRefresh: true,
        includeAuth: false,
      },
    ),

  // Senha
  requestPasswordReset: (email: string) =>
    call<void>('/auth/password/request-reset', {
      method: 'POST',
      body: { email },
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  confirmPasswordReset: (token: string, newPassword: string) =>
    call<void>('/auth/password/confirm-reset', {
      method: 'POST',
      body: { token, newPassword },
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  confirmEmail: (token: string) =>
    call<void>(`/auth/email/confirm/${encodeURIComponent(token)}`, {
      method: 'POST',
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  // Tenants (F02) — autenticadas
  tenantsListMine: () =>
    call<TenantSummaryResponse[]>('/tenants/me'),

  tenantsListMembers: (tenantId: string) =>
    call<MembershipWithUserResponse[]>(
      `/tenants/${encodeURIComponent(tenantId)}/members`,
    ),

  tenantsListInvites: (tenantId: string) =>
    call<InviteResponse[]>(
      `/tenants/${encodeURIComponent(tenantId)}/invites`,
    ),

  tenantsGet: (tenantId: string) =>
    call<TenantResponse>(`/tenants/${encodeURIComponent(tenantId)}`),

  tenantsUpdateSettings: (tenantId: string, settings: Record<string, unknown>) =>
    call<TenantResponse>(`/tenants/${encodeURIComponent(tenantId)}/settings`, {
      method: 'PATCH',
      body: { settings },
    }),

  tenantsInviteMember: (
    tenantId: string,
    input: { email: string; legacyRole: TenantSummaryResponse['legacyRole'] },
  ) =>
    call<InviteMemberResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/invites`,
      { method: 'POST', body: input },
    ),

  tenantsCreateUser: (tenantId: string, input: CreateTenantUserInput) =>
    call<MembershipWithUserResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/users`,
      { method: 'POST', body: input },
    ),

  tenantsUpdateMember: (tenantId: string, userId: string, input: UpdateTenantMemberInput) =>
    call<MembershipWithUserResponse>(
      `/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: input },
    ),

  tenantsRemoveMember: (tenantId: string, userId: string) =>
    call<void>(
      `/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    ),

  tenantsRevokeInvite: (tenantId: string, inviteId: string) =>
    call<void>(
      `/tenants/${encodeURIComponent(tenantId)}/invites/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE' },
    ),

  // F03 — Visibility grants
  listVisibilityGrants: (viewerUserId?: string) =>
    call<VisibilityGrantResponse[]>(
      `/visibility-grants${viewerUserId ? `?viewerUserId=${encodeURIComponent(viewerUserId)}` : ''}`,
    ),

  createVisibilityGrant: (input: CreateVisibilityGrantInput) =>
    call<VisibilityGrantResponse[]>('/visibility-grants', {
      method: 'POST',
      body: input,
    }),

  deleteVisibilityGrant: (id: string) =>
    call<void>(`/visibility-grants/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // tenantsCreate removido em mai/2026: criacao de tenant e privilegio
  // EXCLUSIVO de super_admin via POST /api/admin/tenants
  // (apiClient.adminCreateTenant). Endpoint /tenants foi descontinuado.

  // Convites
  invitePreview: (token: string) =>
    call<InvitePreviewResponse>(
      `/tenants/invites/${encodeURIComponent(token)}/preview`,
      { skipAuthRefresh: true, includeAuth: false },
    ),

  inviteAccept: (token: string) =>
    call<AcceptInviteResponse>(
      `/tenants/invites/${encodeURIComponent(token)}/accept`,
      { method: 'POST' },
    ),

  inviteSignUpAccept: (token: string, input: { name: string; password: string }) =>
    call<SignUpAndAcceptInviteResponse>(
      `/tenants/invites/${encodeURIComponent(token)}/sign-up-and-accept`,
      {
        method: 'POST',
        body: input,
        skipAuthRefresh: true,
        includeAuth: false,
      },
    ),

  // Admin (super_admin)
  adminListTenants: () =>
    call<AdminTenantSummary[]>('/admin/tenants'),

  adminGetTenant: (id: string) =>
    call<AdminTenantDetail>(`/admin/tenants/${encodeURIComponent(id)}`),

  adminCreateTenantWithOwner: (input: CreateTenantWithOwnerInput) =>
    call<CreateTenantWithOwnerResponse>('/admin/tenants', {
      method: 'POST',
      body: input,
    }),

  adminDeactivateTenant: (id: string) =>
    call<AdminTenantSummary>(
      `/admin/tenants/${encodeURIComponent(id)}/deactivate`,
      { method: 'PATCH', body: {} },
    ),

  adminActivateTenant: (id: string) =>
    call<AdminTenantSummary>(
      `/admin/tenants/${encodeURIComponent(id)}/activate`,
      { method: 'PATCH', body: {} },
    ),

  adminDeleteTenant: (id: string) =>
    call<AdminTenantSummary>(
      `/admin/tenants/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  // Platform settings (aparencia global da plataforma)
  adminGetPlatformSettings: () =>
    call<PlatformBranding>('/admin/platform-settings'),

  adminPatchPlatformSettings: (input: PlatformSettingsUpdate) =>
    call<PlatformBranding>('/admin/platform-settings', { method: 'PATCH', body: input }),

  adminUploadPlatformAsset: (kind: PlatformAssetKind, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return callFormData<{ url: string }>(
      `/admin/platform-settings/upload/${encodeURIComponent(kind)}`,
      formData,
    );
  },

  adminRemovePlatformAsset: (kind: PlatformAssetKind) =>
    call<PlatformBranding>(
      `/admin/platform-settings/asset/${encodeURIComponent(kind)}`,
      { method: 'DELETE' },
    ),

  // Integracoes globais da plataforma (super_admin)
  adminGetPluggyIntegration: () =>
    call<PluggyIntegrationStatus>('/admin/integrations/pluggy'),

  adminUpdatePluggyIntegration: (input: UpdatePluggyIntegrationInput) =>
    call<PluggyIntegrationStatus>('/admin/integrations/pluggy', {
      method: 'PUT',
      body: input,
    }),

  adminGetOpenAiIntegration: () =>
    call<OpenAiIntegrationStatus>('/admin/integrations/openai'),

  adminUpdateOpenAiIntegration: (input: UpdateOpenAiIntegrationInput) =>
    call<OpenAiIntegrationStatus>('/admin/integrations/openai', {
      method: 'PUT',
      body: input,
    }),

  // Modelos de IA por finalidade (conversa / imagem / audio)
  adminGetAiModels: () =>
    call<AiModelsIntegrationStatus>('/admin/integrations/ai-models'),

  adminUpdateAiModels: (input: UpdateAiModelsIntegrationInput) =>
    call<AiModelsIntegrationStatus>('/admin/integrations/ai-models', {
      method: 'PUT',
      body: input,
    }),

  // SMTP global da plataforma (e-mails transacionais do sistema)
  adminGetEmailIntegration: () =>
    call<EmailSmtpIntegrationStatus>('/admin/integrations/email'),

  adminUpdateEmailIntegration: (input: UpdateEmailSmtpIntegrationInput) =>
    call<EmailSmtpIntegrationStatus>('/admin/integrations/email', {
      method: 'PUT',
      body: input,
    }),

  adminTestEmailIntegration: (to: string) =>
    call<TestEmailSmtpIntegrationResult>('/admin/integrations/email/test', {
      method: 'POST',
      body: { to },
    }),

  // Storage S3 (MinIO) global da plataforma
  adminGetStorageS3Integration: () =>
    call<StorageS3IntegrationStatus>('/admin/integrations/storage-s3'),

  adminUpdateStorageS3Integration: (input: UpdateStorageS3IntegrationInput) =>
    call<StorageS3IntegrationStatus>('/admin/integrations/storage-s3', {
      method: 'PUT',
      body: input,
    }),

  adminTestStorageS3Integration: () =>
    call<TestStorageS3IntegrationResult>('/admin/integrations/storage-s3/test', {
      method: 'POST',
    }),

  /** Endpoint publico — sem auth. Usado na tela de login e bootstrap. */
  publicGetBranding: () =>
    call<PlatformBranding>('/public/branding', {
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  // Preferencias do usuario (plataforma, sem tenant)
  //
  // Os endpoints aceitam um pageId opcional. Quando omitido (legado), o
  // backend assume 'home'. Mantemos compat com bundles antigos via wrappers.
  meGetDashboardLayout: (pageId?: DashboardPageId) =>
    call<{ layout: WidgetLayout[] | null }>(
      pageId
        ? `/me/dashboard-layout/${encodeURIComponent(pageId)}`
        : '/me/dashboard-layout',
    ),

  meUpdateDashboardLayout: (
    layoutOrPageId: WidgetLayout[] | DashboardPageId,
    maybeLayout?: WidgetLayout[],
  ) => {
    // Overload simples para nao quebrar callers existentes:
    //   meUpdateDashboardLayout(layout)             — legado, page='home'
    //   meUpdateDashboardLayout('clients', layout)  — novo
    const isLegacy = Array.isArray(layoutOrPageId);
    const pageId = isLegacy ? null : (layoutOrPageId as DashboardPageId);
    const layout = isLegacy
      ? (layoutOrPageId as WidgetLayout[])
      : (maybeLayout as WidgetLayout[]);
    return call<{ layout: WidgetLayout[] }>(
      pageId
        ? `/me/dashboard-layout/${encodeURIComponent(pageId)}`
        : '/me/dashboard-layout',
      { method: 'PUT', body: { layout } },
    );
  },

  // Onboarding (tour guiado) — estado por usuario, sem tenant.
  meGetOnboarding: () => call<OnboardingState>('/me/onboarding'),

  meUpdateOnboarding: (patch: {
    dismissed?: boolean;
    dismissedAll?: boolean;
    completeTour?: TourKey;
    /** Botao "Reativar todos os indicadores" — zera completed/dismissedAll no backend. */
    resetTours?: true;
  }) =>
    call<OnboardingState>('/me/onboarding', {
      method: 'PATCH',
      body: patch,
    }),

  // D01 Clientes — leitura
  clientsList: (params: {
    search?: string;
    status?: ClientStatus;
    source?: string;
    type?: 'pf' | 'pj';
    page?: number;
    pageSize?: number;
    sortBy?: 'name' | 'createdAt';
    sortDir?: 'asc' | 'desc';
    birthdaysThisMonth?: boolean;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.source) qs.set('source', params.source);
    if (params.type) qs.set('type', params.type);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    if (params.birthdaysThisMonth) qs.set('birthdaysThisMonth', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<ClientsListResponse>(`/clients${suffix}`);
  },

  clientsGet: (id: string) =>
    call<ClientDetailResponse>(`/clients/${encodeURIComponent(id)}`),

  clientsCreate: (input: CreateClientInput) =>
    call<ClientDetailResponse>('/clients', {
      method: 'POST',
      body: input,
    }),

  clientsUpdate: (id: string, input: UpdateClientInput) =>
    call<ClientDetailResponse>(`/clients/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  clientsDelete: (id: string) =>
    call<void>(`/clients/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  clientsAddContact: (clientId: string, input: CreateContactInput) =>
    call<ClientContact>(
      `/clients/${encodeURIComponent(clientId)}/contacts`,
      { method: 'POST', body: input },
    ),

  clientsUpdateContact: (
    clientId: string,
    contactId: string,
    input: UpdateContactInput,
  ) =>
    call<ClientContact>(
      `/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
      { method: 'PATCH', body: input },
    ),

  clientsDeleteContact: (clientId: string, contactId: string) =>
    call<void>(
      `/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
      { method: 'DELETE' },
    ),

  // Portal do Cliente — lado do escritorio (rodada 2 do Marco 1)
  clientsPortalStatus: (clientId: string) =>
    call<PortalAccessStatusResponse>(
      `/clients/${encodeURIComponent(clientId)}/portal/status`,
    ),

  clientsPortalGrant: (clientId: string, email: string) =>
    call<PortalAccessStatusResponse>(
      `/clients/${encodeURIComponent(clientId)}/portal/grant`,
      { method: 'POST', body: { email } },
    ),

  clientsPortalRevoke: (clientId: string) =>
    call<PortalAccessStatusResponse>(
      `/clients/${encodeURIComponent(clientId)}/portal/revoke`,
      { method: 'POST' },
    ),

  clientsPortalReactivate: (clientId: string) =>
    call<PortalAccessStatusResponse>(
      `/clients/${encodeURIComponent(clientId)}/portal/reactivate`,
      { method: 'POST' },
    ),

  clientsPortalResendLink: (clientId: string) =>
    call<PortalAccessStatusResponse>(
      `/clients/${encodeURIComponent(clientId)}/portal/resend-link`,
      { method: 'POST' },
    ),

  /**
   * "Ver como o cliente ve" (owner/admin): o backend cria a sessao de
   * PREVIEW e seta o cookie `proadv.portal` na propria resposta
   * (`credentials: 'include'` ja e padrao de `call`) — nenhum token
   * transita por URL ou body. Basta abrir `portalPath` em aba nova.
   */
  clientsPortalPreview: (clientId: string) =>
    call<PortalPreviewStartResponse>(
      `/clients/${encodeURIComponent(clientId)}/portal/preview`,
      { method: 'POST' },
    ),

  clientsListAttachments: (clientId: string) =>
    call<ClientAttachment[]>(
      `/clients/${encodeURIComponent(clientId)}/attachments`,
    ),

  /**
   * Upload real de anexo via multipart/form-data. NAO usa call() porque
   * o body eh FormData (browser controla Content-Type com boundary).
   * Mantemos a mesma logica de Authorization Bearer + cookie + 401 retry.
   */
  clientsAttachmentUpload: async (
    clientId: string,
    file: File,
    description?: string | null,
  ): Promise<UploadAttachmentResult> => {
    const form = new FormData();
    form.append('file', file);
    if (description) form.append('description', description);

    const path = `/clients/${encodeURIComponent(clientId)}/attachments`;
    const doSend = async (retry: boolean): Promise<UploadAttachmentResult> => {
      const headers: Record<string, string> = { Accept: 'application/json' };
      try {
        const token = getHooks().getAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      } catch {
        /* hooks nao configurados ainda */
      }
      const res = await fetch(`${API_URL}/api${path}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: form,
      });
      if (res.status === 401 && !retry) {
        const newToken = await performRefresh();
        if (newToken) {
          try {
            getHooks().setAccessToken(newToken);
          } catch {
            /* ignora */
          }
          return doSend(true);
        }
        try {
          getHooks().onSessionLost('refresh_failed');
        } catch {
          /* ignora */
        }
      }
      const text = await res.text();
      const body = safeJson(text);
      if (!res.ok) {
        throw buildError(res.status, body, path);
      }
      return {
        attachment: body as ClientAttachment,
        storageUsedBytes: numHeader(res.headers.get('X-Storage-Used-Bytes')),
        storageQuotaBytes: numHeader(res.headers.get('X-Storage-Quota-Bytes')),
      };
    };
    return doSend(false);
  },

  /**
   * URL absoluta para baixar um anexo. Como o cookie da sessao eh
   * httpOnly em .seasy.com.br, navegacao direta (window.open / anchor
   * href) carrega o cookie e o backend autentica via cookie -> me.
   *
   * Atencao: este endpoint TAMBEM exige Authorization Bearer (JwtAuthGuard).
   * Em produção, o middleware reaproveita a sessao para reemitir o Bearer
   * via /auth/me — mas para download direto via href, o cookie sozinho
   * nao basta. Solucao usada aqui: o front pode optar por fetch + blob
   * download (preserva Authorization) atraves de `clientsAttachmentFetchBlob`.
   */
  clientsAttachmentDownloadUrl: (clientId: string, attachmentId: string): string =>
    `${API_URL}/api/clients/${encodeURIComponent(clientId)}/attachments/${encodeURIComponent(attachmentId)}/download`,

  /**
   * Baixa o anexo via fetch (carrega Authorization Bearer), retorna um
   * Blob URL que o front pode usar em <a href download>. O caller deve
   * dar URL.revokeObjectURL() apos o clique.
   */
  clientsAttachmentFetchBlob: async (
    clientId: string,
    attachmentId: string,
  ): Promise<{ blobUrl: string; filename: string | null }> => {
    const path = `/clients/${encodeURIComponent(clientId)}/attachments/${encodeURIComponent(attachmentId)}/download`;
    const headers: Record<string, string> = {};
    try {
      const token = getHooks().getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* ignora */
    }
    const res = await fetch(`${API_URL}/api${path}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (res.status === 401) {
      const newToken = await performRefresh();
      if (newToken) {
        try {
          getHooks().setAccessToken(newToken);
        } catch {
          /* ignora */
        }
        return apiClient.clientsAttachmentFetchBlob(clientId, attachmentId);
      }
      try {
        getHooks().onSessionLost('refresh_failed');
      } catch {
        /* ignora */
      }
    }
    if (!res.ok) {
      const text = await res.text();
      throw buildError(res.status, safeJson(text), path);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    return {
      blobUrl: URL.createObjectURL(blob),
      filename: match?.[1] ?? null,
    };
  },

  clientsDeleteAttachment: (clientId: string, attachmentId: string) =>
    call<void>(
      `/clients/${encodeURIComponent(clientId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),

  clientsStatsSummary: () =>
    call<ClientsSummaryResponse>('/clients/stats/summary'),

  clientsStatsCharts: () =>
    call<ClientsChartsResponse>('/clients/stats/charts'),

  clientsStatsBirthdays: () =>
    call<ClientsBirthdaysResponse>('/clients/stats/birthdays'),

  // Office config — grupos de acao
  actionGroupsList: (
    params: { active?: ActiveFilter; search?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.active) qs.set('active', params.active);
    if (params.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<ActionGroup[]>(`/office/action-groups${suffix}`);
  },

  actionGroupsGet: (id: string) =>
    call<ActionGroup>(`/office/action-groups/${encodeURIComponent(id)}`),

  actionGroupsCreate: (input: CreateActionGroupInput) =>
    call<ActionGroup>('/office/action-groups', {
      method: 'POST',
      body: input,
    }),

  actionGroupsUpdate: (id: string, input: UpdateActionGroupInput) =>
    call<ActionGroup>(`/office/action-groups/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  actionGroupsDelete: (id: string) =>
    call<void>(`/office/action-groups/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // Office config — tipos de acao
  actionTypesList: (
    params: {
      active?: ActiveFilter;
      actionGroupId?: string;
      search?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.active) qs.set('active', params.active);
    if (params.actionGroupId) qs.set('actionGroupId', params.actionGroupId);
    if (params.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<ActionType[]>(`/office/action-types${suffix}`);
  },

  actionTypesGet: (id: string) =>
    call<ActionType>(`/office/action-types/${encodeURIComponent(id)}`),

  actionTypesCreate: (input: CreateActionTypeInput) =>
    call<ActionType>('/office/action-types', {
      method: 'POST',
      body: input,
    }),

  actionTypesUpdate: (id: string, input: UpdateActionTypeInput) =>
    call<ActionType>(`/office/action-types/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  actionTypesDelete: (id: string) =>
    call<void>(`/office/action-types/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // Office config — etapas processuais
  caseStagesList: (
    params: { active?: ActiveFilter; phase?: CaseStagePhase; search?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.active) qs.set('active', params.active);
    if (params.phase) qs.set('phase', params.phase);
    if (params.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<CaseStage[]>(`/office/case-stages${suffix}`);
  },

  caseStagesGet: (id: string) =>
    call<CaseStage>(`/office/case-stages/${encodeURIComponent(id)}`),

  caseStagesCreate: (input: CreateCaseStageInput) =>
    call<CaseStage>('/office/case-stages', {
      method: 'POST',
      body: input,
    }),

  caseStagesUpdate: (id: string, input: UpdateCaseStageInput) =>
    call<CaseStage>(`/office/case-stages/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  caseStagesDelete: (id: string) =>
    call<void>(`/office/case-stages/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // D02 Processos
  casesList: (params: {
    search?: string;
    status?: CaseStatus;
    stageId?: string;
    clientId?: string;
    actionGroupId?: string;
    actionTypeId?: string;
    nature?: CaseNature;
    tribunalCode?: string;
    vara?: string;
    openedFrom?: string;
    openedTo?: string;
    responsibleUserId?: string;
    page?: number;
    pageSize?: number;
    sortBy?: 'createdAt' | 'openedAt' | 'numeroCnj';
    sortDir?: 'asc' | 'desc';
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.stageId) qs.set('stageId', params.stageId);
    if (params.clientId) qs.set('clientId', params.clientId);
    if (params.actionGroupId) qs.set('actionGroupId', params.actionGroupId);
    if (params.actionTypeId) qs.set('actionTypeId', params.actionTypeId);
    if (params.nature) qs.set('nature', params.nature);
    if (params.tribunalCode) qs.set('tribunalCode', params.tribunalCode);
    if (params.vara) qs.set('vara', params.vara);
    if (params.openedFrom) qs.set('openedFrom', params.openedFrom);
    if (params.openedTo) qs.set('openedTo', params.openedTo);
    if (params.responsibleUserId)
      qs.set('responsibleUserId', params.responsibleUserId);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<CasesListResponse>(`/cases${suffix}`);
  },

  casesGet: (id: string) =>
    call<CaseDetailResponse>(`/cases/${encodeURIComponent(id)}`),

  casesCreate: (input: CreateCaseInput) =>
    call<CaseDetailResponse>('/cases', { method: 'POST', body: input }),

  casesUpdate: (id: string, input: UpdateCaseInput) =>
    call<CaseDetailResponse>(`/cases/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  casesDelete: (id: string) =>
    call<void>(`/cases/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  casesAddParty: (caseId: string, input: CreateCasePartyInput) =>
    call<CaseParty>(`/cases/${encodeURIComponent(caseId)}/parties`, {
      method: 'POST',
      body: input,
    }),

  casesRemoveParty: (caseId: string, partyId: string) =>
    call<void>(
      `/cases/${encodeURIComponent(caseId)}/parties/${encodeURIComponent(partyId)}`,
      { method: 'DELETE' },
    ),

  casesListMovements: (caseId: string) =>
    call<CaseMovement[]>(`/cases/${encodeURIComponent(caseId)}/movements`),

  casesAddMovement: (caseId: string, input: CreateMovementInput) =>
    call<CaseMovement>(`/cases/${encodeURIComponent(caseId)}/movements`, {
      method: 'POST',
      body: input,
    }),

  casesUpdateMovement: (
    caseId: string,
    movementId: string,
    input: UpdateMovementInput,
  ) =>
    call<CaseMovement>(
      `/cases/${encodeURIComponent(caseId)}/movements/${encodeURIComponent(movementId)}`,
      { method: 'PATCH', body: input },
    ),

  casesDeleteMovement: (caseId: string, movementId: string) =>
    call<void>(
      `/cases/${encodeURIComponent(caseId)}/movements/${encodeURIComponent(movementId)}`,
      { method: 'DELETE' },
    ),

  casesStatsSummary: () =>
    call<CasesSummaryResponse>('/cases/stats/summary'),

  // Portal do Cliente — lado do escritorio (docs/PORTAL-CLIENTE.md secao 3.3/5.1)
  casesListPortalAccess: (caseId: string) =>
    call<PortalCaseAccessView[]>(
      `/cases/${encodeURIComponent(caseId)}/portal-access`,
    ),

  casesUpsertPortalAccess: (
    caseId: string,
    clientId: string,
    flags: CaseAccessFlagsInput,
  ) =>
    call<PortalCaseAccessView>(
      `/cases/${encodeURIComponent(caseId)}/portal-access/${encodeURIComponent(clientId)}`,
      { method: 'PUT', body: flags },
    ),

  casesRevokePortalAccess: (caseId: string, clientId: string) =>
    call<{ ok: boolean }>(
      `/cases/${encodeURIComponent(caseId)}/portal-access/${encodeURIComponent(clientId)}`,
      { method: 'DELETE' },
    ),

  casesListPortalMovementOverrides: (caseId: string) =>
    call<PortalMovementOverrideView[]>(
      `/cases/${encodeURIComponent(caseId)}/portal-overrides`,
    ),

  casesUpsertPortalOverride: (
    caseId: string,
    input: {
      clientId: string;
      resourceType: PortalOverrideResourceType;
      resourceId: string;
      effect: PortalOverrideEffect;
    },
  ) =>
    call<{ ok: boolean }>(
      `/cases/${encodeURIComponent(caseId)}/portal-overrides`,
      { method: 'PUT', body: input },
    ),

  casesRemovePortalOverride: (
    caseId: string,
    input: {
      clientId: string;
      resourceType: PortalOverrideResourceType;
      resourceId: string;
    },
  ) =>
    call<{ ok: boolean }>(
      `/cases/${encodeURIComponent(caseId)}/portal-overrides`,
      { method: 'DELETE', body: input },
    ),

  /**
   * J02 — Lookup DataJud por CNJ. Retorna null se o processo nao consta
   * no indice publico do tribunal correspondente.
   */
  casesLookupDataJud: (numeroCnj: string) =>
    call<DataJudLookupResponse | null>(
      `/cases/lookup/datajud?numeroCnj=${encodeURIComponent(numeroCnj.replace(/\D/g, ''))}`,
    ),

  clientCases: (clientId: string) =>
    call<ClientCaseSummary[]>(
      `/clients/${encodeURIComponent(clientId)}/cases`,
    ),

  // J10 Tribunal Registry
  tribunalsList: (
    params: {
      level?: TribunalLevel;
      state?: string;
      system?: TribunalSystem;
      search?: string;
      active?: 'true' | 'false';
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.level) qs.set('level', params.level);
    if (params.state) qs.set('state', params.state);
    if (params.system) qs.set('system', params.system);
    if (params.search) qs.set('search', params.search);
    if (params.active) qs.set('active', params.active);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<Tribunal[]>(`/tribunals${suffix}`);
  },

  tribunalsGet: (code: string) =>
    call<Tribunal>(`/tribunals/${encodeURIComponent(code)}`),

  // J04 v1 — Certificados digitais
  officeCertificatesList: () =>
    call<OfficeCertificateView[]>('/office/certificates'),

  officeCertificatesGet: (id: string) =>
    call<OfficeCertificateView>(
      `/office/certificates/${encodeURIComponent(id)}`,
    ),

  officeCertificatesUpload: (input: UploadCertificateInput) =>
    call<OfficeCertificateView>('/office/certificates', {
      method: 'POST',
      body: input,
    }),

  officeCertificatesDelete: (id: string) =>
    call<void>(`/office/certificates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  officeCertificatesValidate: (id: string) =>
    call<OfficeCertificateView>(
      `/office/certificates/${encodeURIComponent(id)}/validate`,
      { method: 'POST' },
    ),

  // J01 — Intimacoes
  intimacoesList: (params: ListIntimacoesParams = {}) => {
    const qs = new URLSearchParams();
    if (params.lidoEm) qs.set('lidoEm', params.lidoEm);
    if (params.caseId) qs.set('caseId', params.caseId);
    if (params.oabId) qs.set('oabId', params.oabId);
    if (params.dataInicio) qs.set('dataInicio', params.dataInicio);
    if (params.dataFim) qs.set('dataFim', params.dataFim);
    if (params.tribunal) qs.set('tribunal', params.tribunal);
    if (params.search) qs.set('search', params.search);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<IntimacoesListResponse>(`/intimacoes${suffix}`);
  },

  intimacoesGet: (id: string) =>
    call<Intimacao>(`/intimacoes/${encodeURIComponent(id)}`),

  intimacoesMarkAsRead: (id: string) =>
    call<Intimacao>(`/intimacoes/${encodeURIComponent(id)}/lido`, {
      method: 'PATCH',
      body: {},
    }),

  intimacoesLinkCase: (id: string, caseId: string | null) =>
    call<Intimacao>(`/intimacoes/${encodeURIComponent(id)}/vincular-case`, {
      method: 'POST',
      body: { caseId },
    }),

  intimacoesStatsSummary: () =>
    call<IntimacoesSummaryResponse>('/intimacoes/stats/summary'),

  intimacoesListOabsForFilter: () =>
    call<Array<{ id: string; numero: string; uf: string }>>(
      '/intimacoes/filters/oabs',
    ),

  /** URL do PDF da certidao — usar como href ou window.open. */
  intimacoesCertidaoUrl: (id: string) =>
    `${API_URL}/api/intimacoes/${encodeURIComponent(id)}/certidao`,

  // J08 — Termos monitorados
  monitoredTermsList: (active: 'true' | 'false' | 'all' = 'all') =>
    call<MonitoredTerm[]>(
      `/office/monitored-terms${active !== 'all' ? `?active=${active}` : ''}`,
    ),

  monitoredTermsGet: (id: string) =>
    call<MonitoredTerm>(`/office/monitored-terms/${encodeURIComponent(id)}`),

  monitoredTermsCreate: (input: CreateMonitoredTermInput) =>
    call<MonitoredTerm>('/office/monitored-terms', {
      method: 'POST',
      body: input,
    }),

  monitoredTermsUpdate: (id: string, input: UpdateMonitoredTermInput) =>
    call<MonitoredTerm>(`/office/monitored-terms/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  monitoredTermsDelete: (id: string) =>
    call<void>(`/office/monitored-terms/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  monitoredTermsRescan: (id: string) =>
    call<RescanResultResponse>(
      `/office/monitored-terms/${encodeURIComponent(id)}/rescan`,
      { method: 'POST', body: {} },
    ),

  // Customizacoes
  customizationRequestsList: () =>
    call<CustomizationRequest[]>('/customizations/requests'),

  customizationRequestsCreate: (input: CreateCustomizationRequestInput) =>
    call<CustomizationRequest>('/customizations/requests', {
      method: 'POST',
      body: input,
    }),

  monitoredTermsRescanAll: () =>
    call<RescanResultResponse>('/office/monitored-terms/rescan-all', {
      method: 'POST',
      body: {},
    }),

  // J08.2 — Monitoramentos (sujeitos)
  monitoringSubjectsList: (params: ListMonitoringSubjectsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.active) qs.set('active', params.active);
    if (params.kind) qs.set('kind', params.kind);
    if (params.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<MonitoringSubject[]>(`/office/monitoring-subjects${suffix}`);
  },

  monitoringSubjectsGet: (id: string) =>
    call<MonitoringSubject>(
      `/office/monitoring-subjects/${encodeURIComponent(id)}`,
    ),

  monitoringSubjectsCreate: (input: CreateMonitoringSubjectInput) =>
    call<MonitoringSubject>('/office/monitoring-subjects', {
      method: 'POST',
      body: input,
    }),

  monitoringSubjectsUpdate: (
    id: string,
    input: UpdateMonitoringSubjectInput,
  ) =>
    call<MonitoringSubject>(
      `/office/monitoring-subjects/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    ),

  monitoringSubjectsDelete: (id: string) =>
    call<void>(`/office/monitoring-subjects/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  /**
   * Forca polling sincrono do subject. Janela default 7 dias.
   * Substitui o antigo officeOabsPoll.
   */
  monitoringSubjectsSync: (id: string) =>
    call<DjenPollResultResponse>(
      `/office/monitoring-subjects/${encodeURIComponent(id)}/sync`,
      { method: 'POST', body: {} },
    ),

  // J07 — Regras de prazo
  deadlineRulesList: () => call<DeadlineRule[]>('/office/deadline-rules'),

  deadlineRulesGet: (id: string) =>
    call<DeadlineRule>(`/office/deadline-rules/${encodeURIComponent(id)}`),

  deadlineRulesCreate: (input: CreateDeadlineRuleInput) =>
    call<DeadlineRule>('/office/deadline-rules', {
      method: 'POST',
      body: input,
    }),

  deadlineRulesUpdate: (id: string, input: UpdateDeadlineRuleInput) =>
    call<DeadlineRule>(`/office/deadline-rules/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  deadlineRulesDelete: (id: string) =>
    call<void>(`/office/deadline-rules/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  deadlineRulesRestoreDefaults: () =>
    call<void>('/office/deadline-rules/restore-defaults', {
      method: 'POST',
      body: {},
    }),

  // F05 — Notifications
  notificationsList: (
    unreadOnly?: boolean,
    limit?: number,
    offset?: number,
  ) => {
    const qs = new URLSearchParams();
    if (unreadOnly) qs.set('unreadOnly', 'true');
    if (limit != null) qs.set('limit', String(limit));
    if (offset != null) qs.set('offset', String(offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<NotificationsListResponse>(`/notifications${suffix}`);
  },

  notificationsUnreadCount: () =>
    call<NotificationsUnreadCountResponse>('/notifications/unread-count'),

  notificationsMarkAsRead: (id: string) =>
    call<void>(`/notifications/${encodeURIComponent(id)}/read`, {
      method: 'PATCH',
      body: {},
    }),

  notificationsMarkAllAsRead: () =>
    call<void>('/notifications/mark-all-read', {
      method: 'POST',
      body: {},
    }),

  notificationsPreferencesList: () =>
    call<NotificationPreferencesListResponse>(
      '/me/notification-preferences',
    ),

  notificationsPreferencesUpsert: (
    templateKey: string,
    inAppEnabled: boolean,
    emailEnabled: boolean,
  ) =>
    call<void>('/me/notification-preferences', {
      method: 'PATCH',
      body: { templateKey, inAppEnabled, emailEnabled },
    }),

  notificationsTemplatesCatalog: () =>
    call<NotificationTemplatesCatalogResponse>(
      '/notifications/templates-catalog',
    ),

  // D03 Tasks
  tasksList: (params: ListTasksParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<TasksListResponse>(`/tasks${suffix}`);
  },

  tasksGet: (id: string) =>
    call<TaskDetail>(`/tasks/${encodeURIComponent(id)}`),

  tasksStatsSummary: () => call<TaskStatsSummary>('/tasks/stats/summary'),

  tasksCreate: (input: CreateTaskInput) =>
    call<TaskDetail>('/tasks', { method: 'POST', body: input }),

  tasksUpdate: (id: string, input: UpdateTaskInput) =>
    call<TaskDetail>(`/tasks/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  tasksDelete: (id: string) =>
    call<void>(`/tasks/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  tasksComplete: (id: string, completionNote?: string | null) =>
    call<TaskDetail>(`/tasks/${encodeURIComponent(id)}/complete`, {
      method: 'POST',
      body: { completionNote: completionNote ?? null },
    }),

  tasksUncomplete: (id: string) =>
    call<TaskDetail>(`/tasks/${encodeURIComponent(id)}/uncomplete`, {
      method: 'POST',
      body: {},
    }),

  tasksCreateOccurrenceOverride: (
    masterId: string,
    occurrenceDate: string,
    input: CreateOccurrenceOverrideInput,
  ) =>
    call<TaskDetail>(
      `/tasks/${encodeURIComponent(masterId)}/occurrences/${encodeURIComponent(occurrenceDate)}`,
      { method: 'POST', body: input },
    ),

  tasksDeleteOccurrence: (masterId: string, occurrenceDate: string) =>
    call<void>(
      `/tasks/${encodeURIComponent(masterId)}/occurrences/${encodeURIComponent(occurrenceDate)}`,
      { method: 'DELETE' },
    ),

  tasksAddAssignee: (
    id: string,
    userId: string,
    role: TaskAssigneeRole = 'responsible',
  ) =>
    call<unknown>(`/tasks/${encodeURIComponent(id)}/assignees`, {
      method: 'POST',
      body: { userId, role },
    }),

  tasksRemoveAssignee: (id: string, assigneeId: string) =>
    call<void>(
      `/tasks/${encodeURIComponent(id)}/assignees/${encodeURIComponent(assigneeId)}`,
      { method: 'DELETE' },
    ),

  tasksListComments: (id: string) =>
    call<TaskComment[]>(`/tasks/${encodeURIComponent(id)}/comments`),

  tasksAddComment: (id: string, input: CreateTaskCommentInput) =>
    call<TaskComment>(`/tasks/${encodeURIComponent(id)}/comments`, {
      method: 'POST',
      body: input,
    }),

  tasksUpdateComment: (id: string, commentId: string, input: UpdateTaskCommentInput) =>
    call<TaskComment>(
      `/tasks/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
      { method: 'PATCH', body: input },
    ),

  tasksDeleteComment: (id: string, commentId: string) =>
    call<void>(
      `/tasks/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
      { method: 'DELETE' },
    ),

  tasksUploadCommentAttachment: async (
    taskId: string,
    commentId: string,
    file: File,
  ): Promise<TaskCommentAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = (() => {
      try { return getHooks().getAccessToken(); } catch { return null; }
    })();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(
      `${API_URL}/api/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}/attachments`,
      { method: 'POST', credentials: 'include', headers, body: formData },
    );
    if (!res.ok) {
      const body = await res.text().then((t) => { try { return JSON.parse(t); } catch { return t; } });
      throw buildError(res.status, body, `/tasks/${taskId}/comments/${commentId}/attachments`);
    }
    return res.json() as Promise<TaskCommentAttachment>;
  },

  tasksDeleteCommentAttachment: (
    taskId: string,
    commentId: string,
    attachmentId: string,
  ) =>
    call<void>(
      `/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),

  tasksGetHistory: (taskId: string) =>
    call<TaskHistoryEntry[]>(`/tasks/${encodeURIComponent(taskId)}/history`),

  tasksSendEmail: (
    taskId: string,
    input: { toEmail: string; subject: string; body: string },
  ) =>
    call<TaskEmailSend>(`/tasks/${encodeURIComponent(taskId)}/send-email`, {
      method: 'POST',
      body: input,
    }),

  tasksForward: (
    taskId: string,
    input: { toUserId: string; note: string },
  ) =>
    call<{ task: TaskDetail; comment: TaskComment }>(
      `/tasks/${encodeURIComponent(taskId)}/forward`,
      { method: 'POST', body: input },
    ),

  // Endpoints de integracao via outras telas
  casesListTasks: (caseId: string) =>
    call<TaskListItem[]>(`/cases/${encodeURIComponent(caseId)}/tasks`),
  clientsListTasks: (clientId: string) =>
    call<TaskListItem[]>(`/clients/${encodeURIComponent(clientId)}/tasks`),
  intimacoesListTasks: (intimacaoId: string) =>
    call<TaskListItem[]>(
      `/intimacoes/${encodeURIComponent(intimacaoId)}/tasks`,
    ),

  /**
   * Upload de anexo da tarefa via multipart/form-data. Segue o mesmo
   * padrao de clientsAttachmentUpload: nao usa call() porque o body eh
   * FormData (browser controla Content-Type com boundary). Mantemos
   * Authorization Bearer + cookie + retry 401 para sessao expirada.
   *
   * Em caso de erro do servidor:
   *  - 413 (Payload Too Large): arquivo excede limite
   *  - 415 (Unsupported Media Type): mime nao permitido
   *  - 507 (Insufficient Storage): quota do tenant esgotada
   */
  tasksAttachmentUpload: async (
    taskId: string,
    file: File,
  ): Promise<TaskAttachment> => {
    const form = new FormData();
    form.append('file', file);

    const path = `/tasks/${encodeURIComponent(taskId)}/attachments`;
    const doSend = async (retry: boolean): Promise<TaskAttachment> => {
      const headers: Record<string, string> = { Accept: 'application/json' };
      try {
        const token = getHooks().getAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      } catch {
        /* ignora */
      }
      const res = await fetch(`${API_URL}/api${path}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: form,
      });
      if (res.status === 401 && !retry) {
        const newToken = await performRefresh();
        if (newToken) {
          try {
            getHooks().setAccessToken(newToken);
          } catch {
            /* ignora */
          }
          return doSend(true);
        }
        try {
          getHooks().onSessionLost('refresh_failed');
        } catch {
          /* ignora */
        }
      }
      const text = await res.text();
      const body = safeJson(text);
      if (!res.ok) throw buildError(res.status, body, path);
      return body as TaskAttachment;
    };
    return doSend(false);
  },

  /**
   * Baixa o anexo de uma tarefa via fetch (preserva Authorization
   * Bearer). Retorna Blob URL para uso em <a href download>. Caller
   * deve chamar URL.revokeObjectURL apos o uso.
   */
  tasksAttachmentDownload: async (
    taskId: string,
    attachmentId: string,
  ): Promise<{ blobUrl: string; filename: string | null }> => {
    const path = `/tasks/${encodeURIComponent(taskId)}/attachments/${encodeURIComponent(attachmentId)}/download`;
    const headers: Record<string, string> = {};
    try {
      const token = getHooks().getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* ignora */
    }
    const res = await fetch(`${API_URL}/api${path}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (res.status === 401) {
      const newToken = await performRefresh();
      if (newToken) {
        try {
          getHooks().setAccessToken(newToken);
        } catch {
          /* ignora */
        }
        return apiClient.tasksAttachmentDownload(taskId, attachmentId);
      }
      try {
        getHooks().onSessionLost('refresh_failed');
      } catch {
        /* ignora */
      }
    }
    if (!res.ok) {
      const text = await res.text();
      throw buildError(res.status, safeJson(text), path);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    return {
      blobUrl: URL.createObjectURL(blob),
      filename: match?.[1] ?? null,
    };
  },

  tasksAttachmentDelete: (taskId: string, attachmentId: string) =>
    call<void>(
      `/tasks/${encodeURIComponent(taskId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),

  // Task categories
  taskCategoriesList: (params: { active?: 'true' | 'false'; search?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.active) qs.set('active', params.active);
    if (params.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<TaskCategory[]>(`/office/task-categories${suffix}`);
  },

  taskCategoriesCreate: (input: {
    name: string;
    color?: string;
    icon?: string | null;
    orderIndex?: number;
    active?: boolean;
  }) =>
    call<TaskCategory>('/office/task-categories', {
      method: 'POST',
      body: input,
    }),

  taskCategoriesUpdate: (
    id: string,
    input: {
      name?: string;
      color?: string;
      icon?: string | null;
      orderIndex?: number;
      active?: boolean;
    },
  ) =>
    call<TaskCategory>(
      `/office/task-categories/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    ),

  taskCategoriesReorder: (id: string, orderIndex: number) =>
    call<TaskCategory>(
      `/office/task-categories/${encodeURIComponent(id)}/reorder`,
      { method: 'POST', body: { orderIndex } },
    ),

  taskCategoriesDelete: (id: string) =>
    call<void>(`/office/task-categories/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // ---------------------------------------------------------------------------
  // D09 Financeiro
  // ---------------------------------------------------------------------------

  // Bank accounts
  bankAccountsList: () => call<BankAccount[]>('/financial/accounts'),
  bankAccountsGet: (id: string) =>
    call<BankAccount>(`/financial/accounts/${encodeURIComponent(id)}`),
  bankAccountsCreate: (input: {
    name: string;
    type: BankAccountType;
    bankInstitution?: string | null;
    initialBalance?: number;
    isDefault?: boolean;
    active?: boolean;
  }) =>
    call<BankAccount>('/financial/accounts', {
      method: 'POST',
      body: input,
    }),
  bankAccountsUpdate: (
    id: string,
    input: {
      name?: string;
      type?: BankAccountType;
      bankInstitution?: string | null;
      initialBalance?: number;
      isDefault?: boolean;
      active?: boolean;
    },
  ) =>
    call<BankAccount>(`/financial/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),
  bankAccountsDelete: (id: string) =>
    call<void>(`/financial/accounts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  bankAccountsSetDefault: (id: string) =>
    call<void>(`/financial/accounts/${encodeURIComponent(id)}/set-default`, {
      method: 'POST',
      body: {},
    }),

  // Categories
  financialCategoriesList: (
    params: {
      type?: 'income' | 'expense';
      parentId?: 'null' | string;
      active?: 'true' | 'false';
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.type) qs.set('type', params.type);
    if (params.parentId) qs.set('parentId', params.parentId);
    if (params.active) qs.set('active', params.active);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<FinancialCategory[]>(`/financial/categories${suffix}`);
  },
  financialCategoriesTree: (type?: 'income' | 'expense') => {
    const suffix = type ? `?type=${type}` : '';
    return call<FinancialCategoryNode[]>(`/financial/categories/tree${suffix}`);
  },
  financialCategoriesGet: (id: string) =>
    call<FinancialCategory>(`/financial/categories/${encodeURIComponent(id)}`),
  financialCategoriesCreate: (input: {
    name: string;
    type: 'income' | 'expense';
    parentId?: string | null;
    color?: string;
    active?: boolean;
    orderIndex?: number;
  }) =>
    call<FinancialCategory>('/financial/categories', {
      method: 'POST',
      body: input,
    }),
  financialCategoriesUpdate: (
    id: string,
    input: {
      name?: string;
      type?: 'income' | 'expense';
      parentId?: string | null;
      color?: string;
      active?: boolean;
      orderIndex?: number;
    },
  ) =>
    call<FinancialCategory>(
      `/financial/categories/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    ),
  financialCategoriesDelete: (id: string) =>
    call<void>(`/financial/categories/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // Cost centers
  costCentersList: () => call<CostCenter[]>('/financial/cost-centers'),
  costCentersGet: (id: string) =>
    call<CostCenter>(`/financial/cost-centers/${encodeURIComponent(id)}`),
  costCentersCreate: (input: {
    name: string;
    description?: string | null;
    active?: boolean;
  }) =>
    call<CostCenter>('/financial/cost-centers', {
      method: 'POST',
      body: input,
    }),
  costCentersUpdate: (
    id: string,
    input: {
      name?: string;
      description?: string | null;
      active?: boolean;
    },
  ) =>
    call<CostCenter>(`/financial/cost-centers/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),
  costCentersDelete: (id: string) =>
    call<void>(`/financial/cost-centers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // Transactions
  financialTransactionsList: (params: ListTransactionsParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        // Array na querystring como chave repetida (?categoryIds=a&categoryIds=b),
        // nao CSV — mesmo padrao de agendaListEvents (userIds/status), que o
        // backend ja espera via z.union([string, array]) no zod.
        for (const item of v) qs.append(k, String(item));
        continue;
      }
      qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<FinancialTransactionsListResponse>(
      `/financial/transactions${suffix}`,
    );
  },
  financialTransactionsTotals: (params: Omit<ListTransactionsParams, 'page' | 'pageSize' | 'orderBy' | 'orderDir'> = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      if (Array.isArray(v)) {
        for (const item of v) qs.append(k, String(item));
        continue;
      }
      qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<FinancialTransactionsTotalsResponse>(
      `/financial/transactions/totals${suffix}`,
    );
  },
  financialTransactionsGet: (id: string) =>
    call<FinancialTransactionDetail>(
      `/financial/transactions/${encodeURIComponent(id)}`,
    ),
  financialTransactionsCreate: (input: CreateTransactionInput) =>
    call<FinancialTransactionDetail>('/financial/transactions', {
      method: 'POST',
      body: input,
    }),
  financialTransactionsUpdate: (id: string, input: UpdateTransactionInput) =>
    call<FinancialTransactionDetail>(
      `/financial/transactions/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    ),
  financialTransactionsDelete: (id: string) =>
    call<void>(`/financial/transactions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  financialTransactionsDeletePreview: (id: string) =>
    call<FinancialTransactionDeletePreview>(
      `/financial/transactions/${encodeURIComponent(id)}/delete-preview`,
    ),
  financialTransactionsDeleteBatch: (
    id: string,
    scope: FinancialTransactionDeleteScope,
  ) =>
    call<FinancialTransactionDeleteBatchResult>(
      `/financial/transactions/${encodeURIComponent(id)}/delete-batch`,
      { method: 'POST', body: { scope } },
    ),
  financialTransactionsMarkPaid: (
    id: string,
    input: { paymentDate?: string; amount?: number } = {},
  ) =>
    call<FinancialTransactionDetail>(
      `/financial/transactions/${encodeURIComponent(id)}/mark-paid`,
      { method: 'POST', body: input },
    ),
  financialTransactionsMarkUnpaid: (id: string) =>
    call<FinancialTransactionDetail>(
      `/financial/transactions/${encodeURIComponent(id)}/mark-unpaid`,
      { method: 'POST', body: {} },
    ),
  financialTransfer: (input: TransferInput) =>
    call<{ outId: string; inId: string }>('/financial/transactions/transfer', {
      method: 'POST',
      body: input,
    }),

  /**
   * Anexos de transacao (F06). Estrutura espelha tasksAttachment*: a
   * lista vem dentro de financialTransactionsGet(id).attachments;
   * upload/download/delete sao endpoints dedicados.
   */
  financialTransactionAttachmentUpload: async (
    transactionId: string,
    file: File,
    description?: string | null,
  ): Promise<FinancialAttachment> => {
    const form = new FormData();
    form.append('file', file);
    if (description) form.append('description', description);
    const path = `/financial/transactions/${encodeURIComponent(transactionId)}/attachments`;
    const doSend = async (retry: boolean): Promise<FinancialAttachment> => {
      const headers: Record<string, string> = { Accept: 'application/json' };
      try {
        const token = getHooks().getAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      } catch {
        /* ignora */
      }
      const res = await fetch(`${API_URL}/api${path}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: form,
      });
      if (res.status === 401 && !retry) {
        const newToken = await performRefresh();
        if (newToken) {
          try {
            getHooks().setAccessToken(newToken);
          } catch {
            /* ignora */
          }
          return doSend(true);
        }
        try {
          getHooks().onSessionLost('refresh_failed');
        } catch {
          /* ignora */
        }
      }
      const text = await res.text();
      const body = safeJson(text);
      if (!res.ok) throw buildError(res.status, body, path);
      return body as FinancialAttachment;
    };
    return doSend(false);
  },

  financialTransactionAttachmentDownload: async (
    transactionId: string,
    attachmentId: string,
  ): Promise<{ blobUrl: string; filename: string | null }> => {
    const path = `/financial/transactions/${encodeURIComponent(transactionId)}/attachments/${encodeURIComponent(attachmentId)}/download`;
    const headers: Record<string, string> = {};
    try {
      const token = getHooks().getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* ignora */
    }
    const res = await fetch(`${API_URL}/api${path}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (res.status === 401) {
      const newToken = await performRefresh();
      if (newToken) {
        try {
          getHooks().setAccessToken(newToken);
        } catch {
          /* ignora */
        }
        return apiClient.financialTransactionAttachmentDownload(
          transactionId,
          attachmentId,
        );
      }
      try {
        getHooks().onSessionLost('refresh_failed');
      } catch {
        /* ignora */
      }
    }
    if (!res.ok) {
      const text = await res.text();
      throw buildError(res.status, safeJson(text), path);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    return {
      blobUrl: URL.createObjectURL(blob),
      filename: match?.[1] ?? null,
    };
  },

  financialTransactionAttachmentDelete: (
    transactionId: string,
    attachmentId: string,
  ) =>
    call<void>(
      `/financial/transactions/${encodeURIComponent(transactionId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),

  // Listagem por entidades vinculadas
  casesFinancialTransactionsList: (caseId: string) =>
    call<FinancialTransactionListItem[]>(
      `/cases/${encodeURIComponent(caseId)}/transactions`,
    ),
  clientsFinancialTransactionsList: (clientId: string) =>
    call<FinancialTransactionListItem[]>(
      `/clients/${encodeURIComponent(clientId)}/transactions`,
    ),

  // Contracts
  financialContractsList: (
    params: {
      status?: 'active' | 'paused' | 'canceled' | 'completed';
      modality?: 'recurring' | 'installment';
      type?: 'income' | 'expense';
      page?: number;
      pageSize?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<{
      items: FinancialContract[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/financial/contracts${suffix}`);
  },
  financialContractsGet: (id: string) =>
    call<FinancialContractDetail>(
      `/financial/contracts/${encodeURIComponent(id)}`,
    ),
  financialContractsCreate: (input: {
    name: string;
    type: 'income' | 'expense';
    modality: 'recurring' | 'installment';
    totalAmount?: number;
    installmentAmount?: number;
    installmentsCount?: number;
    frequencyMonths?: number;
    firstDueDate: string;
    accountId: string;
    categoryId?: string | null;
    costCenterId: string;
    clientId?: string | null;
    caseId?: string | null;
  }) =>
    call<{ contract: FinancialContract; firstTransactionId: string | null }>(
      '/financial/contracts',
      { method: 'POST', body: input },
    ),
  financialContractsUpdate: (
    id: string,
    input: {
      name?: string;
      installmentAmount?: number;
      frequencyMonths?: number;
      categoryId?: string | null;
      costCenterId?: string;
      clientId?: string | null;
      caseId?: string | null;
    },
  ) =>
    call<FinancialContract>(
      `/financial/contracts/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    ),
  financialContractsCancel: (id: string) =>
    call<FinancialContract>(
      `/financial/contracts/${encodeURIComponent(id)}/cancel`,
      { method: 'POST', body: {} },
    ),
  financialContractsPause: (id: string) =>
    call<FinancialContract>(
      `/financial/contracts/${encodeURIComponent(id)}/pause`,
      { method: 'POST', body: {} },
    ),
  financialContractsResume: (id: string) =>
    call<FinancialContract>(
      `/financial/contracts/${encodeURIComponent(id)}/resume`,
      { method: 'POST', body: {} },
    ),
  financialContractsRenegotiate: (
    id: string,
    input: RenegotiateContractInput,
  ) =>
    call<RenegotiateContractResult>(
      `/financial/contracts/${encodeURIComponent(id)}/renegotiate`,
      { method: 'POST', body: input },
    ),

  // Import Groupings (Fase 2)
  financialImportGroupingsList: (params: {
    page?: number;
    pageSize?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<ImportGroupingsSuggestionsResponse>(
      `/financial/import-groupings/suggestions${suffix}`,
    );
  },
  financialImportGroupingsCount: () =>
    call<{ count: number }>('/financial/import-groupings/count'),
  financialImportGroupingsConfirm: (body: {
    transactionIds: string[];
    contractName: string;
    modality: 'installment';
  }) =>
    call<FinancialContract>('/financial/import-groupings/confirm', {
      method: 'POST',
      body,
    }),

  // Stats
  financialStatsSummary: () =>
    call<FinancialStatsSummary>('/financial/stats/summary'),
  financialStatsChart: (months: number = 6) =>
    call<FinancialChartPoint[]>(
      `/financial/stats/charts/income-expense?months=${months}`,
    ),
  financialStatsTopCategories: (
    type: 'income' | 'expense',
    monthsBack: number = 1,
    limit: number = 5,
  ) =>
    call<FinancialTopCategory[]>(
      `/financial/stats/top-categories?type=${type}&monthsBack=${monthsBack}&limit=${limit}`,
    ),

  // ------------------------------------------------------------------------
  // D07 Documentos / Templates
  // ------------------------------------------------------------------------

  listDocumentTemplates: (query: ListDocumentTemplatesQuery = {}) => {
    const params = new URLSearchParams();
    if (query.categoryId) params.set('categoryId', query.categoryId);
    if (query.area) params.set('area', query.area);
    if (query.status) params.set('status', query.status);
    if (query.search) params.set('search', query.search);
    const qs = params.toString();
    return call<DocumentTemplate[]>(
      `/documents/templates${qs ? `?${qs}` : ''}`,
    );
  },

  getDocumentTemplate: (id: string) =>
    call<DocumentTemplate>(
      `/documents/templates/${encodeURIComponent(id)}`,
    ),

  createDocumentTemplate: (input: CreateDocumentTemplateInput) =>
    call<DocumentTemplate>('/documents/templates', {
      method: 'POST',
      body: input,
    }),

  updateDocumentTemplate: (id: string, input: UpdateDocumentTemplateInput) =>
    call<DocumentTemplate>(
      `/documents/templates/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    ),

  deleteDocumentTemplate: (id: string) =>
    call<void>(`/documents/templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  duplicateDocumentTemplate: (id: string) =>
    call<DocumentTemplate>(
      `/documents/templates/${encodeURIComponent(id)}/duplicate`,
      { method: 'POST' },
    ),

  listDocumentTemplateCategories: () =>
    call<DocumentTemplateCategory[]>('/documents/template-categories'),

  listDocumentTemplateCategoriesTree: () =>
    call<DocumentTemplateCategoryNode[]>(
      '/documents/template-categories/tree',
    ),

  createDocumentTemplateCategory: (input: CreateDocumentTemplateCategoryInput) =>
    call<DocumentTemplateCategory>('/documents/template-categories', {
      method: 'POST',
      body: input,
    }),

  updateDocumentTemplateCategory: (id: string, input: UpdateDocumentTemplateCategoryInput) =>
    call<DocumentTemplateCategory>(
      `/documents/template-categories/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    ),

  deleteDocumentTemplateCategory: (id: string) =>
    call<void>(`/documents/template-categories/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  listDocumentTemplateVariables: (filters?: {
    source?: DocumentTemplateVariableSource;
  }) => {
    const params = new URLSearchParams();
    if (filters?.source) params.set('source', filters.source);
    const qs = params.toString();
    return call<DocumentTemplateVariable[]>(
      `/documents/template-variables${qs ? `?${qs}` : ''}`,
    );
  },

  // ------------------------------------------------------------------------
  // Reports / Templates de relatório
  // ------------------------------------------------------------------------

  listReportTemplates: (query: ListDocumentTemplatesQuery = {}) => {
    const params = new URLSearchParams();
    if (query.categoryId) params.set('categoryId', query.categoryId);
    if (query.area) params.set('area', query.area);
    if (query.status) params.set('status', query.status);
    if (query.search) params.set('search', query.search);
    const qs = params.toString();
    return call<DocumentTemplate[]>(
      `/reports/templates${qs ? `?${qs}` : ''}`,
    );
  },

  getReportTemplate: (id: string) =>
    call<DocumentTemplate>(
      `/reports/templates/${encodeURIComponent(id)}`,
    ),

  createReportTemplate: (input: {
    name: string;
    description?: string | null;
    reportConfig: ReportConfig;
  }) =>
    call<DocumentTemplate>('/reports/templates', {
      method: 'POST',
      body: input,
    }),

  updateReportTemplate: (id: string, input: {
    name?: string;
    description?: string | null;
    reportConfig?: ReportConfig;
    showHeader?: boolean;
    showFooter?: boolean;
  }) =>
    call<DocumentTemplate>(
      `/reports/templates/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input },
    ),

  deleteReportTemplate: (id: string) =>
    call<void>(`/reports/templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  getReportTemplateVariables: (filters?: {
    source?: DocumentTemplateVariableSource;
  }) => {
    const params = new URLSearchParams();
    if (filters?.source) params.set('source', filters.source);
    const qs = params.toString();
    return call<DocumentTemplateVariable[]>(
      `/reports/template-variables${qs ? `?${qs}` : ''}`,
    );
  },

  // Reports — Instances geradas
  generateReportInstance: (input: {
    templateId: string;
    caseId?: string | null;
    clientId?: string | null;
  }) =>
    call<DocumentInstance>('/reports/instances', {
      method: 'POST',
      body: input,
    }),

  listReportInstances: (
    query: {
      caseId?: string;
      clientId?: string;
      templateId?: string;
      status?: DocumentInstanceStatus;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (query.caseId) params.set('caseId', query.caseId);
    if (query.clientId) params.set('clientId', query.clientId);
    if (query.templateId) params.set('templateId', query.templateId);
    if (query.status) params.set('status', query.status);
    const qs = params.toString();
    return call<DocumentInstance[]>(
      `/reports/instances${qs ? `?${qs}` : ''}`,
    );
  },

  getReportInstance: (id: string) =>
    call<DocumentInstance>(
      `/reports/instances/${encodeURIComponent(id)}`,
    ),

  deleteReportInstance: (id: string) =>
    call<void>(`/reports/instances/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  downloadReportInstancePdf: async (
    id: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const path = `/reports/instances/${encodeURIComponent(id)}/pdf`;
    const headers: Record<string, string> = { Accept: 'application/pdf' };
    try {
      const token = getHooks().getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* sem hooks ainda */
    }
    const doFetch = () =>
      fetch(`${API_URL}/api${path}`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
    let res = await doFetch();
    if (res.status === 401) {
      const newToken = await performRefresh();
      if (newToken) {
        try {
          getHooks().setAccessToken(newToken);
        } catch {
          /* ignora */
        }
        headers.Authorization = `Bearer ${newToken}`;
        res = await doFetch();
      } else {
        try {
          getHooks().onSessionLost('refresh_failed');
        } catch {
          /* ignora */
        }
      }
    }
    if (!res.ok) {
      const text = await res.text();
      throw buildError(res.status, safeJson(text), path);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    return {
      blob,
      filename: match?.[1] ?? 'relatorio.pdf',
    };
  },

  // Reports — Automacoes
  listReportAutomations: () =>
    call<ReportAutomation[]>('/reports/automations'),

  getReportAutomation: (id: string) =>
    call<ReportAutomation>(`/reports/automations/${encodeURIComponent(id)}`),

  createReportAutomation: (input: CreateReportAutomationInput) =>
    call<ReportAutomation>('/reports/automations', { method: 'POST', body: input }),

  updateReportAutomation: (id: string, input: UpdateReportAutomationInput) =>
    call<ReportAutomation>(`/reports/automations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  deleteReportAutomation: (id: string) =>
    call<void>(`/reports/automations/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Reports — Entregas
  listReportDeliveries: (query: { status?: ReportDeliveryStatus } = {}) => {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    const qs = params.toString();
    return call<ReportDelivery[]>(`/reports/deliveries${qs ? `?${qs}` : ''}`);
  },

  getReportDelivery: (id: string) =>
    call<ReportDelivery>(`/reports/deliveries/${encodeURIComponent(id)}`),

  approveReportDelivery: (id: string) =>
    call<ReportDelivery>(`/reports/deliveries/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
    }),

  rejectReportDelivery: (id: string) =>
    call<ReportDelivery>(`/reports/deliveries/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
    }),

  sendReportDelivery: (id: string) =>
    call<ReportDelivery>(`/reports/deliveries/${encodeURIComponent(id)}/send`, {
      method: 'POST',
    }),

  // Reports — Instalar modelos padrao
  seedDefaultReportTemplates: () =>
    call<{ created: number; skipped: number }>('/reports/templates/seed-defaults', {
      method: 'POST',
    }),

  // Reports — Analytics Overview
  getReportsAnalyticsOverview: (query?: { from?: string; to?: string }): Promise<ReportsAnalyticsOverview> => {
    const params = new URLSearchParams();
    if (query?.from) params.set('from', query.from);
    if (query?.to) params.set('to', query.to);
    const qs = params.toString();
    return call<ReportsAnalyticsOverview>(`/reports/analytics/overview${qs ? `?${qs}` : ''}`);
  },

  // D07 Importador ADVBOX PDF
  documentTemplatesImportAdvboxPdf: (
    file: File,
    name: string,
    categoryId?: string | null,
  ): Promise<AdvboxImportResult> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name);
    if (categoryId) fd.append('categoryId', categoryId);
    return callFormData<AdvboxImportResult>(
      '/documents/templates/import-advbox-pdf',
      fd,
    );
  },

  // D07 Document Instances (fase A5)
  createDocumentInstance: (input: CreateDocumentInstanceInput) =>
    call<DocumentInstance>('/documents/instances', {
      method: 'POST',
      body: input,
    }),

  listDocumentInstances: (query: ListDocumentInstancesQuery = {}) => {
    const params = new URLSearchParams();
    if (query.clientId) params.set('clientId', query.clientId);
    if (query.caseId) params.set('caseId', query.caseId);
    if (query.templateId) params.set('templateId', query.templateId);
    if (query.status) params.set('status', query.status);
    if (query.search) params.set('search', query.search);
    const qs = params.toString();
    return call<DocumentInstance[]>(
      `/documents/instances${qs ? `?${qs}` : ''}`,
    );
  },

  getDocumentInstance: (id: string) =>
    call<DocumentInstance>(
      `/documents/instances/${encodeURIComponent(id)}`,
    ),

  updateDocumentInstance: (id: string, input: UpdateDocumentInstanceInput) =>
    call<DocumentInstance>(
      `/documents/instances/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: input,
      },
    ),

  deleteDocumentInstance: (id: string) =>
    call<void>(`/documents/instances/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  sendDocumentInstanceEmail: (
    id: string,
    body: { to: string; subject?: string; message?: string },
  ) =>
    call<{ ok: boolean }>(
      `/documents/instances/${encodeURIComponent(id)}/send-email`,
      { method: 'POST', body },
    ),

  /**
   * Baixa o PDF de uma instance D07. Usa fetch + Authorization (nao
   * navegacao direta porque o backend exige Bearer). Devolve o Blob e
   * o filename extraido do Content-Disposition. Caller cria/destroi o
   * objectURL.
   */
  // -------------------------------------------------------------------------
  // D06 Agenda
  // -------------------------------------------------------------------------

  agendaListEvents: (params: {
    from: string;
    to: string;
    userIds?: string[];
    dateField?: 'due' | 'fatal';
    status?: ('pending' | 'completed' | 'overdue')[];
  }): Promise<AgendaEvent[]> => {
    const qs = new URLSearchParams();
    qs.set('from', params.from);
    qs.set('to', params.to);
    if (params.userIds?.length) {
      params.userIds.forEach((id) => qs.append('userIds', id));
    }
    if (params.dateField) qs.set('dateField', params.dateField);
    if (params.status?.length) {
      params.status.forEach((s) => qs.append('status', s));
    }
    return call<AgendaEvent[]>(`/agenda/events?${qs.toString()}`);
  },

  // -------------------------------------------------------------------------
  // Busca global
  // -------------------------------------------------------------------------

  searchGlobal: (q: string, limit?: number): Promise<SearchResults> => {
    const qs = new URLSearchParams({ q });
    if (limit !== undefined) qs.set('limit', String(limit));
    return call<SearchResults>(`/search?${qs.toString()}`);
  },

  // -------------------------------------------------------------------------
  // Importação de dados
  // -------------------------------------------------------------------------

  importsList: (params?: {
    kind?: ImportKind;
    status?: ImportJobStatus;
    page?: number;
    pageSize?: number;
  }): Promise<ImportJobsListResponse> => {
    const qs = new URLSearchParams();
    if (params?.kind) qs.set('kind', params.kind);
    if (params?.status) qs.set('status', params.status);
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
    const q = qs.toString();
    return call<ImportJobsListResponse>(`/imports${q ? `?${q}` : ''}`);
  },

  importsGet: (id: string): Promise<ImportJob> =>
    call<ImportJob>(`/imports/${encodeURIComponent(id)}`),

  importsCreate: (formData: FormData): Promise<ImportJob> =>
    callFormData<ImportJob>('/imports', formData),

  importsParse: (id: string): Promise<ImportJob> =>
    call<ImportJob>(`/imports/${encodeURIComponent(id)}/parse`, { method: 'POST' }),

  importsPreview: (id: string): Promise<ImportPreviewRow[]> =>
    call<ImportPreviewRow[]>(`/imports/${encodeURIComponent(id)}/preview`, { method: 'POST' }),

  importsRun: (id: string): Promise<ImportJob> =>
    call<ImportJob>(`/imports/${encodeURIComponent(id)}/run`, { method: 'POST' }),

  importsCancel: (id: string): Promise<void> =>
    call<void>(`/imports/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  importsRows: (
    id: string,
    params?: { status?: ImportRowStatus; page?: number; pageSize?: number },
  ): Promise<ImportRowsListResponse> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
    const q = qs.toString();
    return call<ImportRowsListResponse>(
      `/imports/${encodeURIComponent(id)}/rows${q ? `?${q}` : ''}`,
    );
  },

  // -------------------------------------------------------------------------
  // J05 — Tribunal Connections
  // -------------------------------------------------------------------------

  tribunalConnectionsList: (): Promise<TribunalConnectionView[]> =>
    call<TribunalConnectionView[]>('/tribunal-connections'),

  tribunalConnectionsGet: (id: string): Promise<TribunalConnectionView> =>
    call<TribunalConnectionView>(`/tribunal-connections/${encodeURIComponent(id)}`),

  tribunalConnectionsCreate: (
    input: CreateTribunalConnectionInput,
  ): Promise<TribunalConnectionView> =>
    call<TribunalConnectionView>('/tribunal-connections', {
      method: 'POST',
      body: input,
    }),

  tribunalConnectionsUpdate: (
    id: string,
    input: UpdateTribunalConnectionInput,
  ): Promise<TribunalConnectionView> =>
    call<TribunalConnectionView>(`/tribunal-connections/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  tribunalConnectionsDelete: (id: string): Promise<void> =>
    call<void>(`/tribunal-connections/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  tribunalConnectionsTestLogin: (
    id: string,
  ): Promise<TestTribunalLoginResponse> =>
    call<TestTribunalLoginResponse>(
      `/tribunal-connections/${encodeURIComponent(id)}/test-login`,
      { method: 'POST' },
    ),

  tribunalConnectionsSyncAcervoPreview: (
    id: string,
  ): Promise<AcervoPreviewResponse> =>
    call<AcervoPreviewResponse>(
      `/tribunal-connections/${encodeURIComponent(id)}/sync-acervo-preview`,
      { method: 'POST' },
    ),

  tribunalConnectionsSyncAcervoImport: (
    id: string,
  ): Promise<AcervoImportResponse> =>
    call<AcervoImportResponse>(
      `/tribunal-connections/${encodeURIComponent(id)}/sync-acervo-import`,
      { method: 'POST' },
    ),

  /**
   * J05 Fase B — Busca individual por CNJ via conexão de tribunal autenticada.
   * RPA: pode demorar 30-90 s (browser navega ao processo).
   */
  tribunalConnectionsLookupCnj: (
    id: string,
    numeroCnj: string,
  ): Promise<TribunalCnjLookupResponse> =>
    call<TribunalCnjLookupResponse>(
      `/tribunal-connections/${encodeURIComponent(id)}/lookup-cnj`,
      { method: 'POST', body: { numeroCnj } },
    ),

  /**
   * J05 — Sincroniza processo existente com o tribunal.
   * Preenche campos vazios da capa e insere movimentações novas (source='tribunal').
   * RPA: pode demorar 30-90 s.
   */
  tribunalConnectionsSyncCase: (
    id: string,
    caseId: string,
  ): Promise<TribunalSyncCaseResponse> =>
    call<TribunalSyncCaseResponse>(
      `/tribunal-connections/${encodeURIComponent(id)}/sync-case`,
      { method: 'POST', body: { caseId } },
    ),

  /**
   * J05 Fase 3 — Dispara o download do binário de um documento de movimentação
   * (RPA no backend). Idempotente: se já baixado retorna jaBaixado=true sem RPA.
   * RPA: pode demorar 20-60 s.
   */
  tribunalConnectionsDownloadDocument: (
    connectionId: string,
    movementDocumentId: string,
  ): Promise<TribunalDownloadDocumentResponse> =>
    call<TribunalDownloadDocumentResponse>(
      `/tribunal-connections/${encodeURIComponent(connectionId)}/download-document`,
      { method: 'POST', body: { movementDocumentId } },
    ),

  /**
   * J05 Fase 3 — URL absoluta para servir o binário de um documento de
   * movimentação já baixado. Exige cookie httpOnly (credentials: include) ou
   * Authorization Bearer. Usar com window.open() ou fetch+blob.
   */
  tribunalMovementDocumentFileUrl: (movementDocumentId: string): string =>
    `${API_URL}/api/tribunal-connections/movement-documents/${encodeURIComponent(movementDocumentId)}/file`,

  /**
   * J05 Fase 3 — Prepara o acesso do viewer ao documento: o backend valida que
   * está baixado, emite um token de download de curta duração e o entrega via
   * cookie httpOnly `proadv.mdoc` (não no corpo nem na URL). Após esta chamada,
   * apontar o `<iframe src>` para `tribunalMovementDocumentFileStreamUrl` — o
   * cookie viaja junto. `credentials: include` (já em `call`) é necessário para
   * o browser aceitar o Set-Cookie.
   */
  tribunalIssueMovementDocumentFileToken: (
    movementDocumentId: string,
  ): Promise<TribunalMovementDocumentFileTokenResponse> =>
    call<TribunalMovementDocumentFileTokenResponse>(
      `/tribunal-connections/movement-documents/${encodeURIComponent(movementDocumentId)}/file-token`,
      { method: 'POST' },
    ),

  /**
   * J05 Fase 3 — URL absoluta do endpoint de STREAMING do binário. Autenticado
   * pelo cookie de download `proadv.mdoc` (setado por
   * `tribunalIssueMovementDocumentFileToken`), enviado automaticamente pelo
   * iframe. O viewer de PDF gerencia o download (Range requests), começando a
   * renderizar antes de ter o arquivo inteiro. Alvo de `<iframe src>`. NÃO usar
   * para HTML (o HTML de fonte externa deve renderizar em iframe sandbox via
   * fetch→blob).
   */
  tribunalMovementDocumentFileStreamUrl: (movementDocumentId: string): string =>
    `${API_URL}/api/tribunal-connections/movement-documents/${encodeURIComponent(movementDocumentId)}/file-stream`,

  /**
   * J05 Fase 3 — Baixa o binário de um documento de movimentação via fetch
   * autenticado (Bearer token em memória + cookie httpOnly), evitando o 401
   * que ocorre ao abrir uma aba nova sem header Authorization.
   * Espelha downloadDocumentInstancePdf.
   */
  downloadMovementDocumentFileBlob: async (
    movementDocumentId: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const path = `/tribunal-connections/movement-documents/${encodeURIComponent(movementDocumentId)}/file`;
    const headers: Record<string, string> = {
      Accept: 'application/pdf,text/html,application/octet-stream,*/*',
    };
    try {
      const token = getHooks().getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* sem hooks ainda — segue sem Authorization */
    }
    const doFetch = () =>
      fetch(`${API_URL}/api${path}`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
    let res = await doFetch();
    if (res.status === 401) {
      const newToken = await performRefresh();
      if (newToken) {
        try {
          getHooks().setAccessToken(newToken);
        } catch {
          /* ignora */
        }
        headers.Authorization = `Bearer ${newToken}`;
        res = await doFetch();
      } else {
        try {
          getHooks().onSessionLost('refresh_failed');
        } catch {
          /* ignora */
        }
      }
    }
    if (!res.ok) {
      const text = await res.text();
      throw buildError(res.status, safeJson(text), path);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    return {
      blob,
      filename: match?.[1] ?? 'documento',
    };
  },

  downloadDocumentInstancePdf: async (
    id: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const path = `/documents/instances/${encodeURIComponent(id)}/pdf`;
    const headers: Record<string, string> = { Accept: 'application/pdf' };
    try {
      const token = getHooks().getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* sem hooks ainda — segue sem Authorization */
    }
    const doFetch = () =>
      fetch(`${API_URL}/api${path}`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
    let res = await doFetch();
    if (res.status === 401) {
      const newToken = await performRefresh();
      if (newToken) {
        try {
          getHooks().setAccessToken(newToken);
        } catch {
          /* ignora */
        }
        headers.Authorization = `Bearer ${newToken}`;
        res = await doFetch();
      } else {
        try {
          getHooks().onSessionLost('refresh_failed');
        } catch {
          /* ignora */
        }
      }
    }
    if (!res.ok) {
      const text = await res.text();
      throw buildError(res.status, safeJson(text), path);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    return {
      blob,
      filename: match?.[1] ?? 'documento.pdf',
    };
  },

  // ---------------------------------------------------------------------------
  // Landing page — leads de early access (endpoint público, sem auth)
  // ---------------------------------------------------------------------------

  createLead: (input: CreateLeadInput) =>
    call<CreateLeadResponse>('/leads', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  // ---------------------------------------------------------------------------
  // F12 Billing — catalogo publico de planos + checkout self-serve (endpoints
  // publicos, sem auth, mesmo padrao do createLead acima)
  // ---------------------------------------------------------------------------

  listPublicPlans: () =>
    call<PublicPlan[]>('/billing/plans', {
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  createCheckout: (input: CreateCheckoutInput) =>
    call<CreateCheckoutResponse>('/billing/checkout', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  // F12 round 6 — checkout transparente (Stripe Payment Element)

  getBillingConfig: () =>
    call<BillingConfigResponse>('/billing/config', {
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  createSubscribe: (input: CreateSubscribeInput) =>
    call<CreateSubscribeResponse>('/billing/subscribe', {
      method: 'POST',
      body: input,
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  // Cupons de desconto (checkout self-serve) — preview publico, read-only.
  previewPromoCode: (code: string) =>
    call<PromoPreviewResponse>(`/billing/promo/${encodeURIComponent(code)}`, {
      skipAuthRefresh: true,
      includeAuth: false,
    }),

  // ---------------------------------------------------------------------------
  // F12 Billing (round 3+) — admin do catalogo de planos (@SuperAdminOnly)
  // ---------------------------------------------------------------------------

  adminListPlans: () => call<AdminPlan[]>('/admin/plans'),

  adminGetPlan: (id: string) =>
    call<AdminPlan>(`/admin/plans/${encodeURIComponent(id)}`),

  adminCreatePlan: (input: AdminCreatePlanInput) =>
    call<AdminPlan>('/admin/plans', {
      method: 'POST',
      body: input,
    }),

  adminUpdatePlan: (id: string, input: AdminUpdatePlanInput) =>
    call<AdminPlan>(`/admin/plans/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  adminDeletePlan: (id: string) =>
    call<AdminPlan>(`/admin/plans/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  /** Cria/atualiza Product+Price no Stripe a partir do plano local. */
  adminSyncPlan: (id: string) =>
    call<AdminPlan>(`/admin/plans/${encodeURIComponent(id)}/sync`, {
      method: 'POST',
    }),

  /** Template com tokens `{{...}}` usado como ponto de partida na UI. */
  adminGetDefaultEmailTemplate: () =>
    call<DefaultEmailTemplateResponse>('/admin/plans/email-template/default'),

  /**
   * Renderiza o HTML do textarea (ou o default, se vazio) com dados de
   * EXEMPLO — usado pelo modal "Visualizar". Nunca envia e-mail.
   */
  adminPreviewEmailTemplate: (input: AdminEmailTemplateSampleInput) =>
    call<DefaultEmailTemplateResponse>('/admin/plans/email-template/preview', {
      method: 'POST',
      body: input,
    }),

  /** Renderiza igual ao preview e ENVIA de verdade para `input.email`. */
  adminTestEmailTemplate: (input: AdminTestEmailTemplateInput) =>
    call<{ ok: true }>('/admin/plans/email-template/test', {
      method: 'POST',
      body: input,
    }),

  // ---------------------------------------------------------------------------
  // F12 Billing — admin do catalogo de cupons de desconto (@SuperAdminOnly)
  // ---------------------------------------------------------------------------

  adminListCoupons: () => call<AdminCoupon[]>('/admin/coupons'),

  adminGetCoupon: (id: string) =>
    call<AdminCoupon>(`/admin/coupons/${encodeURIComponent(id)}`),

  adminCreateCoupon: (input: AdminCreateCouponInput) =>
    call<AdminCoupon>('/admin/coupons', {
      method: 'POST',
      body: input,
    }),

  adminUpdateCoupon: (id: string, input: AdminUpdateCouponInput) =>
    call<AdminCoupon>(`/admin/coupons/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  /** Soft delete: active = false. Nunca remove a linha nem o Coupon/PromotionCode no Stripe. */
  adminDeleteCoupon: (id: string) =>
    call<AdminCoupon>(`/admin/coupons/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // ---------------------------------------------------------------------------
  // Storage S3 + quotas (rodada 3b) — gerenciador de disco (@SuperAdminOnly)
  // ---------------------------------------------------------------------------

  adminStorageUsage: (query: AdminStorageUsageQuery) =>
    call<AdminStorageUsageResult>(
      `/admin/storage/usage?${new URLSearchParams({
        page: String(query.page ?? 1),
        pageSize: String(query.pageSize ?? 50),
        sortBy: query.sortBy ?? 'usagePercent',
        sortDir: query.sortDir ?? 'desc',
      }).toString()}`,
    ),

  adminSetStorageOverride: (tenantId: string, overrideBytes: number | null) =>
    call<StorageOverrideResult>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/storage-override`,
      { method: 'PUT', body: { overrideBytes } },
    ),

  /** Atalho para overrideBytes: null — volta a herdar a quota do plano. */
  adminClearStorageOverride: (tenantId: string) =>
    call<StorageOverrideResult>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/storage-override`,
      { method: 'DELETE' },
    ),

  // ---------------------------------------------------------------------------
  // Storage S3 + quotas (rodada 3b) — consumo do proprio escritorio
  // ---------------------------------------------------------------------------

  /** GET /api/storage/usage — nunca expoe a fonte da quota (override/plano/env/default). */
  storageUsage: () => call<StorageUsageResponse>('/storage/usage'),

  // ---------------------------------------------------------------------------
  // Passo B — area de assinatura do cliente (autenticado, tenant context
  // normal — diferente das rotas /billing/* acima, que sao publicas).
  // ---------------------------------------------------------------------------

  getAccountSubscription: () =>
    call<AccountSubscriptionResponse>('/account/subscription'),

  getAccountInvoices: () =>
    call<AccountInvoicesResponse>('/account/invoices'),

  /** Cria a Billing Portal Session do tenant logado e devolve a url de redirect. */
  createBillingPortalSession: () =>
    call<{ url: string }>('/account/billing-portal', { method: 'POST' }),

  /**
   * Passo B rodada 2 — assinatura self-service do tenant JA LOGADO (sem
   * plano ativo). Mesmo shape de resposta do createSubscribe publico
   * (clientSecret/subscriptionId/mode); `tenantId` vem do cookie/token via
   * @TenantId() no backend, nunca do body. 409 se ja existe assinatura
   * ativa; 400 se o plano for invalido/sem price sincronizado.
   */
  accountSubscribe: (planCode: string) =>
    call<CreateSubscribeResponse>('/account/subscribe', {
      method: 'POST',
      body: { planCode },
    }),

  // ---------------------------------------------------------------------------
  // Passo B rodada 3 — gerenciar pagamento por UI propria (substitui o
  // Billing Portal como fluxo principal; portal vira fallback secundario).
  // Dados de cartao nunca tocam este client — so o Payment Element/
  // SetupIntent no browser, que devolve um paymentMethodId (pm_...).
  // ---------------------------------------------------------------------------

  getPaymentMethod: () =>
    call<AccountPaymentMethodResponse>('/account/payment-method'),

  /** Cria o SetupIntent para o Payment Element (modo setup) coletar um novo cartao. */
  setupPaymentMethod: () =>
    call<{ clientSecret: string }>('/account/payment-method/setup', {
      method: 'POST',
    }),

  /** Define um cartao (ja coletado via SetupIntent) como default do customer+subscription. */
  setDefaultPaymentMethod: (paymentMethodId: string) =>
    call<{ ok: true }>('/account/payment-method/default', {
      method: 'POST',
      body: { paymentMethodId },
    }),

  /** Retry do pagamento em atraso: clientSecret da invoice aberta, ou nada a pagar. */
  payOpenInvoice: () =>
    call<AccountPayResponse>('/account/subscription/pay', { method: 'POST' }),

  // ---------------------------------------------------------------------------
  // Configuracao de e-mail SMTP por escritorio
  // ---------------------------------------------------------------------------

  tenantEmailSettingsGet: (): Promise<TenantEmailSettingsView | null> =>
    call<TenantEmailSettingsView | null>('/tenant-email-settings'),

  tenantEmailSettingsUpsert: (
    input: UpsertTenantEmailSettingsInput,
  ): Promise<TenantEmailSettingsView> =>
    call<TenantEmailSettingsView>('/tenant-email-settings', {
      method: 'PUT',
      body: input,
    }),

  tenantEmailSettingsDelete: (): Promise<void> =>
    call<void>('/tenant-email-settings', { method: 'DELETE' }),

  tenantEmailSettingsTest: (
    input?: TestTenantEmailSettingsInput,
  ): Promise<TestTenantEmailSettingsResponse> =>
    call<TestTenantEmailSettingsResponse>('/tenant-email-settings/test', {
      method: 'POST',
      body: input ?? {},
    }),

  tenantEmailSettingsSetEnabled: (
    input: SetTenantEmailEnabledInput,
  ): Promise<TenantEmailSettingsView> =>
    call<TenantEmailSettingsView>('/tenant-email-settings/enabled', {
      method: 'PATCH',
      body: input,
    }),

  // Assistente de IA — confirma uma acao proposta (human-in-the-loop). Usa o
  // call() padrao (Bearer + cookie + refresh em 401 + ApiError com status).
  assistantConfirmAction: (
    proposalId: string,
  ): Promise<AssistantConfirmActionResponse> =>
    call<AssistantConfirmActionResponse>(
      `/assistant/actions/${encodeURIComponent(proposalId)}/confirm`,
      { method: 'POST' },
    ),

  // Assistente de IA — cria a proposta de exclusao de um lancamento a partir
  // do id ESCOLHIDO PELO CLIQUE do usuario num candidato de select_transaction
  // (transaction-selection-card.tsx). O id nunca vem do modelo.
  assistantFinancialDeleteProposal: (
    transactionId: string,
  ): Promise<AssistantFinancialDeleteProposalResponse> =>
    call<AssistantFinancialDeleteProposalResponse>(
      '/assistant/actions/financial-delete-proposal',
      { method: 'POST', body: { transactionId } },
    ),

  // ---------------------------------------------------------------------
  // Portal do Cliente — caixa de entrada de upload, lado ESCRITORIO
  // (docs/PORTAL-CLIENTE.md secao 6, Marco 4). Rota `/portal-uploads`,
  // FORA de `/portal/*` (aquele prefixo e do cliente final, guard
  // diferente) — espelha apps/api/.../portal/office/portal-upload-review.controller.ts.
  // ---------------------------------------------------------------------

  portalUploadsList: (params: {
    status?: PortalUploadStatusFilter;
    page?: number;
    pageSize?: number;
  } = {}): Promise<PortalUploadsListResponse> => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return call<PortalUploadsListResponse>(`/portal-uploads${suffix}`);
  },

  portalUploadsApprove: (
    id: string,
    input: ApprovePortalUploadInput,
  ): Promise<PortalUploadInboxItem> =>
    call<PortalUploadInboxItem>(`/portal-uploads/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: input,
    }),

  portalUploadsReject: (id: string, reason: string): Promise<PortalUploadInboxItem> =>
    call<PortalUploadInboxItem>(`/portal-uploads/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: { reason },
    }),

  /**
   * Baixa o arquivo pendente para inspecao via fetch + blob (mesmo padrao
   * de `clientsAttachmentFetchBlob`) — o endpoint exige Authorization
   * Bearer (JwtAuthGuard), navegacao direta por href nao carrega o token.
   */
  portalUploadsDownloadBlob: async (
    id: string,
  ): Promise<{ blobUrl: string; filename: string | null }> => {
    const path = `/portal-uploads/${encodeURIComponent(id)}/download`;
    const headers: Record<string, string> = {};
    try {
      const token = getHooks().getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      /* ignora */
    }
    const res = await fetch(`${API_URL}/api${path}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (res.status === 401) {
      const newToken = await performRefresh();
      if (newToken) {
        try {
          getHooks().setAccessToken(newToken);
        } catch {
          /* ignora */
        }
        return apiClient.portalUploadsDownloadBlob(id);
      }
      try {
        getHooks().onSessionLost('refresh_failed');
      } catch {
        /* ignora */
      }
    }
    if (!res.ok) {
      const text = await res.text();
      throw buildError(res.status, safeJson(text), path);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    return {
      blobUrl: URL.createObjectURL(blob),
      filename: match?.[1] ?? null,
    };
  },
};

// ---------------------------------------------------------------------------
// Importação de dados (módulo /imports)
// ---------------------------------------------------------------------------

export type ImportKind = 'clients' | 'cases' | 'tasks' | 'financial';

export type ImportJobStatus =
  | 'pending'
  | 'parsing'
  | 'preview'
  | 'importing'
  | 'done'
  | 'failed';

export type ImportRowStatus =
  | 'pending'
  | 'imported'
  | 'skipped'
  | 'error';

export interface ImportJob {
  id: string;
  tenantId: string;
  createdById: string;
  kind: ImportKind;
  status: ImportJobStatus;
  fileName: string;
  fileStorageObjectId: string | null;
  options: Record<string, unknown>;
  totalRows: number | null;
  processedRows: number | null;
  successRows: number | null;
  skippedRows: number | null;
  errorRows: number | null;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportJobRow {
  id: string;
  tenantId: string;
  importJobId: string;
  rowIndex: number;
  status: ImportRowStatus;
  rawData: Record<string, unknown> | null;
  mappedData: Record<string, unknown> | null;
  createdEntityId: string | null;
  createdEntityKind: string | null;
  notes: string | null;
  errorMessage: string | null;
}

export interface ImportJobsListResponse {
  items: ImportJob[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ImportRowsListResponse {
  items: ImportJobRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ImportPreviewRow {
  rowIndex: number;
  mappedData: Record<string, unknown> | null;
  errors: string[];
  warnings: string[];
}

// ---------------------------------------------------------------------------
// J05 — Tribunal Connections
// ---------------------------------------------------------------------------

export type TribunalAdapterStatus =
  | 'pending'
  | 'active'
  | 'invalid_password'
  | 'invalid_2fa'
  | 'session_expired'
  | 'disconnected'
  | 'error';

export interface TribunalConnectionView {
  id: string;
  tribunalCode: string;
  sistema: string;
  grau: number;
  oabUf: string;
  oabNumber: string;
  loginUsername: string | null;
  status: TribunalAdapterStatus | 'pending';
  hasTotpSeed: boolean;
  hasPersistedProfile: boolean;
  lastCheckAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTribunalConnectionInput {
  tribunalCode: string;
  sistema: 'eproc' | 'pje' | 'jpe' | 'esaj' | 'projudi';
  grau: 1 | 2;
  oabUf: string;
  oabNumber: string;
  loginUsername?: string;
  senha: string;
  totpSeed?: string;
}

export interface UpdateTribunalConnectionInput {
  oabUf?: string;
  oabNumber?: string;
  loginUsername?: string;
  /** Deixar vazio ou omitir para manter a senha atual. */
  senha?: string;
  /** String vazia remove o seed; omitir mantém o atual. */
  totpSeed?: string;
}

export interface TestTribunalLoginResponse {
  success: boolean;
  status: TribunalAdapterStatus;
  message: string;
  durationMs: number;
}

export interface AcervoPreviewCaseItem {
  numeroCnj: string | null;
  numeroCnjDigits: string | null;
  classe: string | null;
  orgaoJulgador: string | null;
  partes: { nome: string; polo: 'ativo' | 'passivo' | 'outro' | null }[];
  ultimoAndamento: string | null;
  ultimoAndamentoData: string | null;
  jaExiste: boolean;
}

export interface AcervoPreviewResponse {
  acervoUrl: string;
  total: number;
  novos: number;
  existentes: number;
  reauthenticated: boolean;
  note?: string;
  menuLinks: { texto: string; href: string }[];
  paginaFinal: string;
  processos: AcervoPreviewCaseItem[];
}

export type AcervoImportAcao = 'criado' | 'atualizado' | 'sem-mudanca';

export interface AcervoImportDetalhe {
  numeroCnj: string | null;
  acao: AcervoImportAcao;
  camposPreenchidos?: string[];
}

export interface AcervoImportResponse {
  criados: number;
  atualizados: number;
  ignorados: number;
  detalhes: AcervoImportDetalhe[];
}

// J05 Fase B — lookup individual por CNJ via conexão de tribunal
export interface TribunalCnjLookupAssunto {
  codigo: string | null;
  descricao: string;
  principal: boolean;
}

export interface TribunalCnjLookupParty {
  papel: string;
  nome: string;
}

export interface TribunalCnjLookupResponse {
  found: boolean;
  numeroProcesso: string | null;
  classe: string | null;
  competencia: string | null;
  dataAutuacao: string | null;
  situacao: string | null;
  orgaoJulgador: string | null;
  magistrado: string | null;
  valorCausa: number | null;
  sigilo: string | null;
  assuntos: TribunalCnjLookupAssunto[];
  partes: TribunalCnjLookupParty[];
  reauthenticated: boolean;
}

/** Resposta do POST /tribunal-connections/:id/download-document. */
export interface TribunalDownloadDocumentResponse {
  storageObjectId: string;
  contentType: string;
  filename: string;
  jaBaixado: boolean;
}

/**
 * Resposta do POST
 * /tribunal-connections/movement-documents/:id/file-token.
 * O token trafega via cookie httpOnly `proadv.mdoc` (não aqui). O front só
 * precisa saber que o cookie foi setado para então apontar o `<iframe src>` ao
 * endpoint de streaming.
 */
export interface TribunalMovementDocumentFileTokenResponse {
  expiresInSec: number;
}

/** Resposta do POST /tribunal-connections/:id/sync-case. */
export interface TribunalSyncCaseResponse {
  found: boolean;
  capaPreenchida: string[];
  movimentacoesNovas: number;
  movimentacoesExistentes: number;
  sigilo: string | null;
  reauthenticated: boolean;
}

export type ApiClient = typeof apiClient;

// ---------------------------------------------------------------------------
// Landing — Early Access Lead
// ---------------------------------------------------------------------------

export interface CreateLeadInput {
  name: string;
  email: string;
  whatsapp: string;
  context?: string;
  /** Honeypot anti-bot: humanos deixam vazio; se vier preenchido o backend descarta. */
  website?: string;
  source: 'landing';
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export interface CreateLeadResponse {
  id: string;
}

// ---------------------------------------------------------------------------
// F12 Billing — catalogo publico de planos + checkout self-serve
// ---------------------------------------------------------------------------

/** Espelha a projecao de PlansService.listPublic (nunca expoe campos internos). */
export interface PublicPlan {
  code: string;
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  interval: 'month' | 'year';
  trialDays: number;
  /** Array livre (jsonb): strings de copy ou objetos {label, included} — decisao de UI. */
  features: unknown[];
  sortOrder: number;
}

/** Espelha createCheckoutSchema (packages/validators/src/billing.ts). */
export interface CreateCheckoutInput {
  planCode: string;
  name: string;
  email: string;
  officeName: string;
  phone?: string;
  /** Honeypot anti-bot: humanos deixam vazio; se vier preenchido o backend descarta. */
  website?: string;
}

export interface CreateCheckoutResponse {
  url: string;
}

/** GET /billing/config — chave publicavel do Stripe. Pode vir vazia em dev. */
export interface BillingConfigResponse {
  publishableKey: string;
}

/**
 * Espelha createSubscribeSchema (packages/validators/src/billing.ts) — o
 * backend FORKOU esse schema a partir de createCheckoutSchema so para
 * acrescentar `promoCode` (cupom de desconto do checkout self-serve).
 * Por isso aqui tambem e uma interface propria em vez de reusar
 * CreateCheckoutInput (que serve o fluxo de checkout redirect, sem cupom).
 */
export interface CreateSubscribeInput extends CreateCheckoutInput {
  /** Codigo do cupom de desconto, normalizado (trim + uppercase). Opcional. */
  promoCode?: string;
}

export interface CreateSubscribeResponse {
  clientSecret: string;
  subscriptionId: string;
  /** 'payment' = cobranca imediata (confirmPayment); 'setup' = trial sem cobranca agora (confirmSetup). */
  mode: 'payment' | 'setup';
}

/**
 * GET /billing/promo/:code — preview publico de cupom (BillingService.previewPromoCode).
 * Espelha o shape de retorno do backend: NUNCA inclui id interno do Stripe,
 * so os dados de desconto necessarios para a UI.
 */
export interface PromoPreviewResponse {
  valid: boolean;
  discountType?: 'percent' | 'amount';
  percentOff?: number;
  amountOffCents?: number;
  duration?: 'once' | 'forever' | 'repeating';
}

// ---------------------------------------------------------------------------
// F12 Billing (round 3+) — admin do catalogo de planos
// ---------------------------------------------------------------------------

/** Espelha `Plan` (packages/database/src/schema/plans.ts) — linha completa, uso admin. */
export interface AdminPlan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  interval: 'month' | 'year';
  trialDays: number;
  features: unknown[];
  /**
   * HTML customizado do e-mail de primeiro acesso deste plano (tokens
   * {{nome}}/{{email}}/{{senha}}/{{link}}/{{plano}}/{{preco}}/{{trialDias}}/
   * {{escritorio}}). null/vazio -> BillingService usa o template padrao.
   */
  firstAccessEmailHtml: string | null;
  stripeProductId: string | null;
  stripePriceId: string | null;
  active: boolean;
  sortOrder: number;
  /**
   * Storage S3 + quotas (rodada 3). Quota de armazenamento do plano em
   * bytes; null = plano não define quota própria (cai no fallback
   * override > plano > env > default, ver StorageQuotaService).
   */
  storageQuotaBytes: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Espelha createPlanSchema (packages/validators/src/billing.ts). */
export interface AdminCreatePlanInput {
  /** kebab-case (a-z, 0-9, hifen) — identificador estavel usado no checkout. */
  code: string;
  name: string;
  description?: string | null;
  priceCents: number;
  currency?: string;
  interval?: 'month' | 'year';
  trialDays?: number;
  features?: unknown[];
  firstAccessEmailHtml?: string | null;
  active?: boolean;
  sortOrder?: number;
  /** null = sem quota própria (herda o fallback); omitido no update = não muda. */
  storageQuotaBytes?: number | null;
}

/** Espelha updatePlanSchema (createPlanSchema.partial()) — `code` na pratica fica read-only na UI. */
export type AdminUpdatePlanInput = Partial<AdminCreatePlanInput>;

// ---------------------------------------------------------------------------
// Storage S3 + quotas (rodada 3b) — gerenciador de disco + consumo do escritorio.
// Espelha apps/api/src/modules/storage/{storage-admin.service,storage-usage.controller}.ts
// ---------------------------------------------------------------------------

export type StorageQuotaSource = 'override' | 'plan' | 'env' | 'default';

export interface AdminStorageUsageItem {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  planCode: string | null;
  planName: string | null;
  quotaBytes: number;
  quotaSource: StorageQuotaSource;
  usedBytes: number;
  usagePercent: number;
}

export interface AdminStorageUsageResult {
  items: AdminStorageUsageItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminStorageUsageQuery {
  page?: number;
  pageSize?: number;
  sortBy?: 'usagePercent' | 'usedBytes' | 'quotaBytes' | 'tenantName';
  sortDir?: 'asc' | 'desc';
}

export interface StorageOverrideResult {
  tenantId: string;
  overrideBytes: number | null;
}

export interface StorageUsageBreakdownItem {
  resourceType: string;
  usedBytes: number;
  count: number;
}

/** GET /storage/usage (escritorio) — nunca inclui a fonte da quota. */
export interface StorageUsageResponse {
  quotaBytes: number;
  usedBytes: number;
  availableBytes: number;
  usagePercent: number;
  breakdown: StorageUsageBreakdownItem[];
}

/** GET /admin/plans/email-template/default */
export interface DefaultEmailTemplateResponse {
  html: string;
}

/**
 * Body comum de POST /admin/plans/email-template/preview e /test. Espelha
 * previewEmailTemplateSchema (packages/validators/src/billing.ts) — `html`
 * e o conteudo ATUAL do textarea (vazio/ausente -> template padrao no
 * backend); `name`/`priceCents`/`trialDays` espelham os campos do form
 * (nao o plano persistido).
 */
export interface AdminEmailTemplateSampleInput {
  html?: string;
  name?: string;
  priceCents?: number;
  trialDays?: number;
}

/** Body de POST /admin/plans/email-template/test — preview + destinatario real. */
export interface AdminTestEmailTemplateInput extends AdminEmailTemplateSampleInput {
  email: string;
}

// ---------------------------------------------------------------------------
// F12 Billing — admin do catalogo de cupons de desconto
// ---------------------------------------------------------------------------

export type AdminCouponDiscountType = 'percent' | 'amount';
export type AdminCouponDuration = 'once' | 'forever' | 'repeating';

/** Espelha `DiscountCoupon` (packages/database/src/schema/discount-coupons.ts) — linha completa, uso admin. */
export interface AdminCoupon {
  id: string;
  code: string;
  name: string;
  description: string | null;
  discountType: AdminCouponDiscountType;
  percentOff: number | null;
  amountOffCents: number | null;
  currency: string;
  duration: AdminCouponDuration;
  durationInMonths: number | null;
  stripeCouponId: string | null;
  stripePromotionCodeId: string | null;
  active: boolean;
  maxRedemptions: number | null;
  expiresAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Espelha createCouponSchema (packages/validators/src/coupons.ts). */
export interface AdminCreateCouponInput {
  /** Normalizado (trim + uppercase) no backend — imutavel no Stripe apos criado. */
  code: string;
  name: string;
  description?: string | null;
  discountType: AdminCouponDiscountType;
  percentOff?: number;
  amountOffCents?: number;
  currency?: string;
  duration: AdminCouponDuration;
  durationInMonths?: number;
  maxRedemptions?: number;
  /** ISO string — o backend converte para Date antes de gravar/enviar ao Stripe. */
  expiresAt?: string;
  active?: boolean;
}

/** Espelha updateCouponSchema (createCouponSchema.partial()) — `code` na pratica fica read-only na UI. */
export type AdminUpdateCouponInput = Partial<AdminCreateCouponInput>;

// ---------------------------------------------------------------------------
// Passo B — area de assinatura do cliente
// ---------------------------------------------------------------------------

export type AccountSubscriptionStatus =
  | 'active'
  | 'suspended'
  | 'trial'
  | 'canceled'
  | 'past_due';

/** Espelha AccountSubscription (apps/api/src/modules/billing/subscription.service.ts). */
export interface AccountSubscriptionResponse {
  status: AccountSubscriptionStatus;
  plan: {
    name: string;
    priceCents: number;
    currency: string;
    interval: string;
  } | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
}

/** Espelha AccountInvoiceItem — valores em centavos, `created` como ISO string. */
export interface AccountInvoiceItem {
  id: string;
  number: string | null;
  created: string;
  amountPaid: number;
  amountDue: number;
  currency: string;
  status: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface AccountInvoicesResponse {
  items: AccountInvoiceItem[];
}

// ---------------------------------------------------------------------------
// Passo B rodada 3 — gerenciar pagamento por UI propria
// ---------------------------------------------------------------------------

/** Metadados do cartao default do tenant, para exibir na UI (nunca o PAN completo). */
export interface AccountCardView {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface AccountPaymentMethodResponse {
  card: AccountCardView | null;
}

/** Espelha AccountPayResult (apps/api/src/modules/billing/subscription.service.ts). */
export type AccountPayResponse =
  | { clientSecret: string; mode: 'payment' }
  | { nothingToPay: true };

// ---------------------------------------------------------------------------
// Configuracao de e-mail SMTP por escritorio
// ---------------------------------------------------------------------------

export type EmailTestStatus = 'success' | 'failure';

export interface TenantEmailSettingsView {
  id: string;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  fromEmail: string;
  fromName: string | null;
  replyTo: string | null;
  signature: string | null;
  enabled: boolean;
  passwordConfigured: boolean;
  lastTestedAt: string | null;
  lastTestStatus: EmailTestStatus | null;
  lastTestError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertTenantEmailSettingsInput {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  /** Omitir ou deixar vazio para manter senha atual. Trocar credencial zera `enabled`. */
  password?: string;
  fromEmail: string;
  fromName?: string;
  replyTo?: string;
  signature?: string;
}

export interface TestTenantEmailSettingsInput {
  /** Destinatario do e-mail de teste. Omitir usa o e-mail do usuario logado. */
  to?: string;
}

export interface TestTenantEmailSettingsResponse {
  ok: boolean;
  message: string;
  durationMs: number;
}

export interface SetTenantEmailEnabledInput {
  enabled: boolean;
}
